import { CheckOutlined, DownOutlined, UploadOutlined } from '@ant-design/icons';
import { CrudFilter, IResourceComponentsProps, useCreate, useNotification } from '@refinedev/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Modal,
  notification,
  Radio,
  Typography,
  Upload
} from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { UploadChangeParam, UploadFile } from 'antd/es/upload';
import dayjs from 'dayjs';
import { formatEther } from 'ethers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useModalForm } from 'sunflower-antd';

import { createCompany, useGetInfoByUserCode } from '../../api/company';
import { getExchangeRate, useGetAllMetadataCurrency, useGetAvlBalanceInternal, useGetConfigurationFee, useGetExchangeRate } from '../../api/sendmoney';
import { httpClient, ROOT_URL } from '../../App';
import { ETHPrice, PaymentTypes } from '../../common/payment-types.const';
import {
  estimateMintGas,
  getPayTokenContract,
  mintTokens,
} from '../../common/smart-contracts/helpers/PayToken/PayToken';
import { PayToken } from '../../common/smart-contracts/typechain-types';
import {
  AutoComplete
} from '../../components/inputs/autocomplete';
import { Option } from '../../components/inputs/autocomplete/autocomplete';
import { AutocompleteCustomerInput } from '../../components/inputs/autocomplete/autocompleteCustomerInput';
import {
  getPurposeOfRemittanceOptionLabel,
  purposeOfRemittanceItems,
  purposeOfRemittancePlaceholder,
} from '../../components/inputs/select/purposeOfRemittance.constants';
import { SelectField } from '../../components/inputs/select/select';
import { PaymentConfirmationModal } from '../../components/modal/paymentConfirmationModal';
import { TOKEN_KEY } from '../../components/providers/authProvider';
import { IBankAccount } from '../../interfaces/bankAccounts';
import { IBank, IWallet } from '../../interfaces/banks';
import { ICompany } from '../../interfaces/companies';
import { ICustomer } from '../../interfaces/customer';
import { allowedUploadTypes } from '../../types/allowedAttachedDocuments.type';
import PaymentStatus from '../../types/paymentStatus.type';
import { acceptUploadFileTypes } from '../../utils/acceptUploadFileTypes';
import { beforeUpload } from '../../utils/beforeUpload';
import { getETHPrice } from '../../utils/blockchain';
import { buildBankAccountValue, buildCurrencyValue, buildWalletValue, mapBankAccountItem, mapCompanyItem, mapCompanyUserItem, mapCurrencyItem, mapWalletItem } from '../../utils/edit.constants';
import {
  bankRules,
  clearFieldErrorMessageOnChange,
  companyRules,
  companySearchFieldNameFilter,
  currencyRules,
  dateOfTransactionRules,
  getFileListFromEvent,
  paymentRequestPurposeOfRemittanceRules,
  paymentRequestServiceProviderRules,
  walletRules
} from '../../utils/form.constants';
import { useIsAdminRole } from '../../utils/hooks/useIdentityAdmin.hook';
import { useNotificationOkCancel } from '../../utils/hooks/useNotificationOkCancel.hook';
import useResponsive from '../../utils/hooks/useResponsive';
import { confirmModalDifferentCurrenciesProps, confirmModalUnsavedChangesProps } from '../components/constants';
import { DefaultDatePicker, getDateValueProps } from '../components/datePicker';
import Details from '../components/details/details';
import {
  bankDetailsFields,
  companyBeneficiaryDetailsFields,
  companyDetailsFields,
  customerDetailsFieldsSmartUID,
  walletDetailsFields
} from '../components/details/details.constants';
import { IPaymentFormValues } from '../components/interface';
import ICurrency from '../currencies/currency.interface';
import { dateOfTransactionFieldStyle, inputFormStyles, uploadButtonStyle } from '../styles/style';
import { NewAccountModalCreate } from './addAccount';
import {
  abaRoutingNumberRules,
  bankStateBranchNumberRules,
  branchNumberRules,
  ibanRules,
  isBankAccountFieldOptional,
  isIbanRequiredByCountryAndCurrency,
} from './addAccount.constants';
import { NewBankModalCreate } from './addBank';
import { NewCompanyModalCreate } from './addCompany';

// Payment method constants - moved outside component to avoid recreation on every render
const listPaymentMethod = {
  PORTFOLIO: 'PORTFOLIO',
  DIRECTER_TRANSFER: 'DIRECTER_TRANSFER',
};

export const PaymentRequestCreatePage: React.FC<IResourceComponentsProps> = () => {
  const navigate = useNavigate();
  const { open: openNotification } = useNotification();
  const { mutate: createPaymentRequest, isLoading: isCreating } = useCreate();
  const isAdminRole = useIsAdminRole();
  const { isTablet } = useResponsive();

  const hasInputBeneficiaryRef = useRef(false)
  const hasInputPaymentRef = useRef(false);

  const [specialInstructionsDraft, setSpecialInstructionsDraft] = useState<string | undefined>('');
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [showCompanyDetails, setShowCompanyDetails] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [showWalletDetails, setShowWalletDetails] = useState(false);
  const [showServiceProviderDetails, setShowServiceProviderDetails] = useState(false);
  const [nextStatus, setNextStatus] = useState<PaymentStatus>(PaymentStatus.DRAFT);
  const [paymentType, setPaymentType] = useState<PaymentTypes>(PaymentTypes.Fiat);
  const [currencyData, setCurrencyData] = useState<any>();
  const [paymentCurrencyData, setPaymentCurrencyData] = useState<any>();
  const [customerData, setCustomerData] = useState<ICustomer>();
  const [companyData, setCompanyData] = useState<ICompany>();
  const [serviceProviderData, setServiceProviderData] = useState<ICompany>();
  const [bankData, setBankData] = useState<IBank | null>();
  const [accountData, setAccountData] = useState<IBankAccount>();
  const [walletData, setWalletData] = useState<IWallet | null>();
  const [providerBankAccountData, setProviderBankAccountData] = useState<any>();
  const [providerWalletData, setProviderWalletData] = useState<any>();
  const [amountData, setAmountData] = useState<string | null>(null);
  const [instructionsData, setInstructionsData] = useState<string | undefined>();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isFormEmpty, setIsFormEmpty] = useState(true);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [paymentConfirmationModalOpened, setPaymentConfirmationModalOpened] = useState<boolean>(false);
  const [api, alertContextHolder] = notification.useNotification({ placement: 'bottom' });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [userWallet, setUserWallet] = useState<string>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [payTokenContract, setPayTokenContract] = useState<PayToken>();
  const [fee, setFee] = useState<string>();
  const paymentConfirmed = useRef<(value: PromiseLike<null> | null) => void>();
  const paymentRejected = useRef<(reason?: any) => void>();
  const [ethPrice, setETHPrice] = useState<ETHPrice>();
  const [exchangeRate, setExchangeRate] = useState<number>(1);
  const [beneficiaryCurrencyId, setBeneficiaryCurrencyId] = useState<number | undefined>();
  const [beneficiaryCurrencyData, setBeneficiaryCurrencyData] = useState<any>();
  const [paymentCurrencyId, setPaymentCurrencyId] = useState<number | undefined>();
  const [lastChangedField, setLastChangedField] = useState<'beneficiary' | 'payment'>('beneficiary');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [direction, setDirection] = useState<'BEN_TO_PAY' | 'PAY_TO_BEN'>('PAY_TO_BEN');
  const [configFee, setConfigFee] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [transferFee, setTransferFee] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [serviceFee, setServiceFee] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [feeConfigId, setFeeConfigId] = useState<number | undefined>(undefined);
  const [beneficiaryValueError, setBeneficiaryValueError] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [paymentValueError, setPaymentValueError] = useState(false);
  const [isChangingCurrency, setIsChangingCurrency] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [hasSetDefault, setHasSetDefault] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [beneficiaryDecimal, setBeneficiaryDecimal] = useState(2);
  const [paymentDecimal, setPaymentDecimal] = useState(2);
  const [feesPayer, setFeesPayer] = useState<'SENDER' | 'BENEFICIARY'>('SENDER');
  const [preservedBeneficiaryError, setPreservedBeneficiaryError] = useState<string | null>(null);

  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState<string>(listPaymentMethod.DIRECTER_TRANSFER);
  const [disabledPortfolio, setDisabledPortfolio] = useState(false);
  const [isFirstLoadBalance, setIsFirstLoadBalance] = useState(true);

  const [beneficiaryCompanyId, setBeneficiaryCompanyId] = useState<number | undefined>(undefined);
  const [infoByUserCode, setInfoByUserCode] = useState<any>(null);
  const [showReviewFee, setShowReviewFee] = useState(false)

  const [bankAccounts, setBankAccounts] = useState<IBankAccount[] | undefined>(undefined)
  const [isBankLoading, setIsBankLoading] = useState(false)
  const [wallets, setWallets] = useState<IWallet[] | undefined>(undefined)
  const [isWalletLoading, setIsWalletLoading] = useState(false)
  const [isBeneficiaryLoading, setIsBeneficiaryLoading] = useState(false)

  // Refs for tracking state (matching sample logic)
  const isSettingDefaultRef = useRef(false);
  const hasSetDefaultRef = useRef(false);
  const isChangingCurrencyRef = useRef(false);
  const hasUserEditedInputRef = useRef(false);
  const preservedBeneficiaryErrorRef = useRef<string | null>(null);

  // Currency state with symbols (matching sample)
  const [sendCurrency, setSendCurrency] = useState<number | undefined>(undefined);
  const [paymentCurrency, setPaymentCurrency] = useState<number | undefined>(undefined);
  const [sendCurrencySymbol, setSendCurrencySymbol] = useState<string>('USD');
  const [paymentCurrencySymbol, setPaymentCurrencySymbol] = useState<string>('EUR');
  const [lastChange, setLastChange] = useState<'BENEFICIARY' | 'SENDER'>('BENEFICIARY');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [beneficiaryAmount, setBeneficiaryAmount] = useState<number>(0);
  const amount = useWatch('amount', { form });
  const beneficiaryValue = useWatch('beneficiary_value', { form });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const paymentValue = useWatch('payment_value', { form });
  const cryptoWallet = useWatch('cryptoWallet', { form });
  const currencyCode: string = useWatch('currencyCode', { form });
  const beneficiary = useWatch('beneficiary', { form });

  // Get all currencies
  const { data: allCurrenciesData } = useGetAllMetadataCurrency();

  const { mutate: fetchInfoByUserCode } = useGetInfoByUserCode({
    onSuccess: (response: { data: { data: any } }) => {
      setInfoByUserCode(response?.data?.data);
      form.setFields([
        {
          name: "smartUID",
          errors: undefined,
        },
      ]);
    },
    onError: (error: { message: string; reason_code: string }) => {
      setInfoByUserCode(
        error?.reason_code === "ERR00010"
          ? "This SMART UID does not exist in SMART+"
          : error?.message
      );
      if (error?.reason_code === "ERR00010") {
        form.setFields([
          {
            name: "smartUID",
            errors: ["This SMART UID does not exist in SMART+"],
          },
        ]);
      } else {
        form.setFields([
          {
            name: "smartUID",
            errors: [error?.message || ""],
          },
        ]);
      }
    },
  });

  const refetchPaymentType = useCallback(() => {
    if (!isBeneficiaryLoading) {
      return;
    }

    const walletsLoaded = !isWalletLoading && wallets !== undefined;
    const bankAccountsLoaded = !isBankLoading && bankAccounts !== undefined;

    if (walletsLoaded && wallets !== undefined) {
      setIsBeneficiaryLoading(false);
      if (companyData?.refUser?.smartUID && wallets.length <= 0) {
        setPaymentType(PaymentTypes.EWallet)
      }
      if (wallets.length > 0) {
        setPaymentType(PaymentTypes.Fiat)
      }
    } else if (bankAccountsLoaded && bankAccounts !== undefined) {
      setIsBeneficiaryLoading(false);
      if (companyData?.refUser?.smartUID && bankAccounts.length <= 0) {
        setPaymentType(PaymentTypes.EWallet)
      }
      if (bankAccounts.length > 0) {
        setPaymentType(PaymentTypes.Fiat)
      }
    }
  }, [isWalletLoading, wallets, isBeneficiaryLoading, companyData, isBankLoading, bankAccounts]);

  useEffect(() => {
    refetchPaymentType();
  }, [isWalletLoading, wallets, isBankLoading, bankAccounts, isBeneficiaryLoading, companyData, refetchPaymentType]);

  useEffect(() => {
    if (paymentType === PaymentTypes.EWallet) {
      if (companyData?.refUser?.smartUID) {
        form.setFieldValue('smartUID', companyData?.refUser?.smartUID)
        fetchInfoByUserCode(companyData?.refUser?.smartUID);
      } else {
        form.setFieldValue('smartUID', undefined)
        setInfoByUserCode(null)
      }
    }
  }, [companyData?.refUser?.smartUID, paymentType, fetchInfoByUserCode, form]);

  useEffect(() => {
    if (customerData?.smartUID) {
      const specialInstructionsDraftData = `Ref ${customerData?.smartUID}`
      setSpecialInstructionsDraft(specialInstructionsDraftData);
      form.setFieldValue('specialInstructions', specialInstructionsDraftData);
    }
  }, [customerData?.smartUID, form]);

  const allCurrencies = useMemo(() => {
    const data = allCurrenciesData?.data?.data || [];
    const result = Array.isArray(data) ? data : [];
    return result;
  }, [allCurrenciesData]);

  // Custom mapCurrencyItem with icon and symbol for beneficiary currency
  const mapCurrencyItemWithIcon = useCallback((currency: ICurrency, index: number) => {
    const currencyId = (currency as any).id;
    // Check if currency id exists in allCurrencies list
    const currencyInList = allCurrencies.find((item: any) => item.id === currencyId);

    if (currencyInList) {
      // If found in list, display with icon and symbol
      const displaySymbol = currencyInList.symbol || currencyInList.code || buildCurrencyValue(currency);
      const isSelected = sendCurrency === currencyId || beneficiaryCurrencyId === currencyId;

      // Use symbol as value for display (form field will show symbol)
      // But keep currencyId in data for onSelect handler
      return {
        value: displaySymbol, // Use symbol as value so form field shows symbol
        key: String(currencyId) || index, // Use ID as key for uniqueness
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>

              <span style={{ fontWeight: 500 }}>{displaySymbol}</span>
            </div>
            {isSelected && (
              <CheckOutlined style={{ color: '#1890ff', fontSize: 16 }} />
            )}
          </div>
        ),
        data: currency, // Keep original currency data with ID
      };
    }

    // Otherwise use default mapping
    return mapCurrencyItem(currency, index);
  }, [allCurrencies, sendCurrency, beneficiaryCurrencyId]);

  // Custom mapCurrencyItem with icon and symbol for beneficiary currency
  const mapCurrencyUserItemWithIcon = useCallback((currency: ICurrency, index: number) => {
    const currencyId = (currency as any).id;
    // Check if currency id exists in allCurrencies list
    const currencyInList = allCurrencies.find((item: any) => item.id === currencyId);

    if (currencyInList) {
      // If found in list, display with icon and symbol
      const displaySymbol = currencyInList.symbol || currencyInList.code || buildCurrencyValue(currency);
      const isSelected = paymentCurrency === currencyId || paymentCurrencyId === currencyId;

      // Use symbol as value for display (form field will show symbol)
      // But keep currencyId in data for onSelect handler
      return {
        value: displaySymbol, // Use symbol as value so form field shows symbol
        key: String(currencyId) || index, // Use ID as key for uniqueness
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>

              <span style={{ fontWeight: 500 }}>{displaySymbol}</span>
            </div>
            {isSelected && (
              <CheckOutlined style={{ color: '#1890ff', fontSize: 16 }} />
            )}
          </div>
        ),
        data: currency, // Keep original currency data with ID
      };
    }

    // Otherwise use default mapping
    return mapCurrencyItem(currency, index);
  }, [allCurrencies, paymentCurrency, paymentCurrencyId]);

  // Get exchange rate - use sendCurrency and paymentCurrency (matching sample)
  // Only call API when customer is selected and both currencies have values
  const fromCurrency = paymentCurrency || paymentCurrencyId;
  const toCurrency = sendCurrency || beneficiaryCurrencyId;
  const shouldFetchRate = !!customerData?.id && !!fromCurrency && !!toCurrency;


  const { data: rateData, refetch: refetchRate } = useGetExchangeRate(
    { from: fromCurrency, to: toCurrency },
    {
      enabled: shouldFetchRate,
      onSuccess: (response: any) => {
        const rate = response?.data?.data || response?.data || 1;
        setExchangeRate(rate);
      },
    }
  );

  // Get available balance for Portfolio payment method
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: avlBalance, refetch: refetchBalance } = useGetAvlBalanceInternal(
    paymentCurrencyId || paymentCurrency || 0,
    customerData?.id || 0
  );

  useEffect(() => {
    if (rateData?.data?.data) {
      const newRate = rateData.data.data;
      if (newRate !== exchangeRate) {

        setExchangeRate(newRate);
        // Recalculate values when exchange rate changes
        if (!isChangingCurrency && !isSettingDefault && beneficiaryCurrencyId && paymentCurrencyId) {
          const currentBeneficiaryValue = form.getFieldValue('beneficiary_value');
          const currentPaymentValue = form.getFieldValue('payment_value');
          const beneficiaryValueNum = Number(currentBeneficiaryValue) || 0;
          const paymentValueNum = Number(currentPaymentValue) || 0;

          if (lastChangedField === 'beneficiary' && beneficiaryValueNum > 0) {
            // Recalculate payment value based on beneficiary value
            const paymentCurrency = allCurrencies.find((item: any) => item.id === paymentCurrencyId);
            if (paymentCurrency) {
              const calculatedPayment = (beneficiaryValueNum / newRate).toFixed(paymentCurrency.decimals || 2);

              form.setFieldValue('payment_value', calculatedPayment);
              setPaymentAmount(Number(calculatedPayment));
              // Trigger validation after recalculation
              setTimeout(() => {
                form.validateFields(['payment_value']).catch(() => {
                  // Validation errors are expected - ignore
                });
              }, 100);
            }
          } else if (lastChangedField === 'payment' && paymentValueNum > 0) {
            // Recalculate beneficiary value based on payment value
            const beneficiaryCurrency = allCurrencies.find((item: any) => item.id === beneficiaryCurrencyId);
            if (beneficiaryCurrency) {
              const calculatedBeneficiary = (paymentValueNum * newRate).toFixed(beneficiaryCurrency.decimals || 2);

              form.setFieldValue('beneficiary_value', calculatedBeneficiary);
              setBeneficiaryAmount(Number(calculatedBeneficiary));
              // Trigger validation after recalculation
              setTimeout(() => {
                form.validateFields(['beneficiary_value']).catch(() => {
                  // Validation errors are expected - ignore
                });
              }, 100);
            }
          }
        }
      }
    } else if (rateData?.data) {
      const newRate = rateData.data;
      if (newRate !== exchangeRate) {
        setExchangeRate(newRate);
        // Recalculate values when exchange rate changes
        if (!isChangingCurrency && !isSettingDefault && beneficiaryCurrencyId && paymentCurrencyId) {
          const currentBeneficiaryValue = form.getFieldValue('beneficiary_value');
          const currentPaymentValue = form.getFieldValue('payment_value');
          const beneficiaryValueNum = Number(currentBeneficiaryValue) || 0;
          const paymentValueNum = Number(currentPaymentValue) || 0;

          if (lastChangedField === 'beneficiary' && beneficiaryValueNum > 0) {
            const paymentCurrency = allCurrencies.find((item: any) => item.id === paymentCurrencyId);
            if (paymentCurrency) {
              const calculatedPayment = (beneficiaryValueNum / newRate).toFixed(paymentCurrency.decimals || 2);

              form.setFieldValue('payment_value', calculatedPayment);
              setPaymentAmount(Number(calculatedPayment));
              // Trigger validation after recalculation
              setTimeout(() => {
                form.validateFields(['payment_value']).catch(() => {
                  // Validation errors are expected - ignore
                });
              }, 100);
            }
          } else if (lastChangedField === 'payment' && paymentValueNum > 0) {
            const beneficiaryCurrency = allCurrencies.find((item: any) => item.id === beneficiaryCurrencyId);
            if (beneficiaryCurrency) {
              const calculatedBeneficiary = (paymentValueNum * newRate).toFixed(beneficiaryCurrency.decimals || 2);

              form.setFieldValue('beneficiary_value', calculatedBeneficiary);
              setBeneficiaryAmount(Number(calculatedBeneficiary));
              // Trigger validation after recalculation
              setTimeout(() => {
                form.validateFields(['beneficiary_value']).catch(() => {
                  // Validation errors are expected - ignore
                });
              }, 100);
            }
          }
        }
      }
    }
  }, [rateData, exchangeRate, isChangingCurrency, isSettingDefault, beneficiaryCurrencyId, paymentCurrencyId, lastChangedField, allCurrencies, form]);

  useEffect(() => {
    if (!fromCurrency || !toCurrency) return
    const intervalId = setInterval(() => {
      refetchRate()
    }, 60000)
    return () => clearInterval(intervalId)
  }, [fromCurrency, toCurrency, refetchRate])

  // Get configuration fee
  const { mutate: fetchConfigurationFee, isLoading: loadingFee } = useGetConfigurationFee({
    onSuccess: (response: any) => {
      const feeData = response?.data?.data || response?.data;
      if (feeData) {
        setConfigFee(feeData);
      }
    },
    onError: () => {
      // Error handled by hook
    },
  });

  // Helper functions for rounding
  const roundTo = useCallback((value: number, decimals: number) => {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  }, []);

  const roundUp = useCallback((value: number, decimals: number) => {
    const factor = Math.pow(10, decimals);
    return Math.ceil(value * factor) / factor;
  }, []);

  const roundDown = useCallback((value: number, decimals: number) => {
    const factor = Math.pow(10, decimals);
    return Math.floor(value * factor) / factor;
  }, []);

  /**
   * Round down a value with minimum precision guarantee.
   * If the result is 0.00, increases decimal places until a non-zero value is found.
   * @param value - The value to round down
   * @param decimals - Initial decimal places (default: 2)
   * @param maxDecimals - Maximum decimal places to try (default: 18)
   * @returns The rounded down value with appropriate precision
   */
  const roundDownWithMinimum = useCallback((
    value: number,
    decimals = 2,
    maxDecimals = 18
  ): { value: number; decimals: number } => {
    let currentDecimals = decimals;
    let result = roundDown(value, currentDecimals);

    // If result is 0 and value is not 0, increase decimal places
    while (result === 0 && value !== 0 && currentDecimals < maxDecimals) {
      currentDecimals += 2; // Increase by 2 decimal places each time
      result = roundDown(value, currentDecimals);
    }

    return { value: result, decimals: currentDecimals };
  }, [roundDown]);

  const formatNumber = useCallback((value: number | string, decimals: number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0';
    return num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }, []);

  // Helper function to find currency by symbol
  const findCurrencyBySymbol = useCallback((symbol: string) => {
    return allCurrencies.find((item: any) => item.symbol === symbol || item.code === symbol);
  }, [allCurrencies]);

  // Get max limit for currency
  const getMaxLimit = useCallback((currencyId: number | undefined) => {
    if (!currencyId || !allCurrencies?.length) return null;
    const currency = allCurrencies.find((item: any) => item.id === currencyId);
    if (!currency) return null;
    const symbol = currency.symbol || currency.code;
    switch (symbol) {
      case 'ETH':
        return 10000000;
      case 'USDT':
      case 'DAI':
      case 'USD':
      case 'EUR':
        return 10000000000;
      case 'HKD':
        return 77800000000;
      case 'AUD':
        return 15000000000;
      case 'JPY':
        return 15479000000;
      case 'CHF':
        return 8000000000;
      case 'CNY':
        return 70700000000;
      case 'AED':
        return 36700000000;
      case 'SGD':
        return 12900000000;
      case 'GBP':
        return 7400000000;
      default:
        return null;
    }
  }, [allCurrencies]);

  // Handler functions matching sample logic
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleChangeBeneficiaryValue = useCallback((_e: React.ChangeEvent<HTMLInputElement>) => {
    hasInputBeneficiaryRef.current = true;
    // Mark that user has manually entered, no longer default
    isSettingDefaultRef.current = false;
    hasUserEditedInputRef.current = true;
    // Reset currency change flag when user manually changes value
    isChangingCurrencyRef.current = false;
    // Clear preserved error when user manually enters a new value
    preservedBeneficiaryErrorRef.current = null;
    setPreservedBeneficiaryError(null);
    setLastChange('BENEFICIARY');
    setLastChangedField('beneficiary');
    setDirection('BEN_TO_PAY');
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleChangePaymentValue = useCallback((_e: React.ChangeEvent<HTMLInputElement>) => {
    hasInputPaymentRef.current = true;
    // Mark that user has manually entered, no longer default
    isSettingDefaultRef.current = false;
    hasUserEditedInputRef.current = true;
    // Reset currency change flag when user manually changes value
    isChangingCurrencyRef.current = false;
    setLastChange('SENDER');
    setLastChangedField('payment');
    setDirection('PAY_TO_BEN');
  }, []);

  const handleBlurSend = useCallback(async () => {
    hasInputBeneficiaryRef.current = true;
    const rawValue = form.getFieldValue('beneficiary_value');
    const val = Number(rawValue);

    // Clear preserved error when user blurs (user has finished editing)
    preservedBeneficiaryErrorRef.current = null;
    setPreservedBeneficiaryError(null);

    if (val && val > 0) {
      setBeneficiaryAmount(val);
      const formattedValue = val.toFixed(beneficiaryDecimal);
      form.setFieldsValue({
        beneficiary_value: formattedValue,
      });
      setDirection('BEN_TO_PAY');
      setLastChange('BENEFICIARY');
      setLastChangedField('beneficiary');

      // Always validate beneficiary_value on blur to ensure errors are shown
      const performValidation = async () => {
        try {
          await form.validateFields(['beneficiary_value']);

          // If beneficiary_value is valid, calculate payment_value
          if (paymentCurrencyId && exchangeRate && beneficiaryCurrencyId) {
            const paymentCurrency = allCurrencies.find((item: any) => item.id === paymentCurrencyId);
            if (paymentCurrency) {
              const calculatedPaymentValue = (val * (1 / exchangeRate)).toFixed(paymentDecimal);
              const calculatedPaymentValueNum = Number(calculatedPaymentValue);

              // Check max limit for payment currency
              const maxLimitPayment = getMaxLimit(paymentCurrencyId);

              if (maxLimitPayment !== null && calculatedPaymentValueNum > maxLimitPayment) {
                form.setFieldsValue({
                  payment_value: calculatedPaymentValue,
                });
                setPaymentAmount(calculatedPaymentValueNum);
                setTimeout(() => {
                  form.validateFields(['payment_value']);
                }, 0);
              } else {
                form.setFieldsValue({
                  payment_value: calculatedPaymentValue,
                });
                setPaymentAmount(calculatedPaymentValueNum);
                setTimeout(() => {
                  form.validateFields(['payment_value']);
                }, 0);
              }
            }
          }
        } catch (error) {
          // If beneficiary_value has error, don't calculate payment_value
          form.resetFields(['payment_value']);
          setPaymentAmount(0);
        }
      };

      setTimeout(() => performValidation(), 0);
      setTimeout(() => performValidation(), 200);
      setTimeout(() => performValidation(), 500);
    } else {
      form.resetFields(['payment_value']);
      setPaymentAmount(0);
      setBeneficiaryAmount(0);
      setTimeout(() => {
        form.validateFields(['beneficiary_value']).catch(() => {
          // Validation errors are expected - ignore
        });
      }, 200);
      setTimeout(() => {
        form.validateFields(['beneficiary_value']).catch(() => {
          // Validation errors are expected - ignore
        });
      }, 500);
    }
  }, [form, beneficiaryDecimal, paymentDecimal, paymentCurrencyId, exchangeRate, beneficiaryCurrencyId, allCurrencies, getMaxLimit]);

  const handleBlurPayment = useCallback(async () => {
    hasInputPaymentRef.current = true;
    const rawValue = form.getFieldValue('payment_value');
    const val = Number(rawValue);

    if (val && val > 0) {
      setPaymentAmount(val);
      const formattedValue = val.toFixed(paymentDecimal);
      form.setFieldsValue({ payment_value: formattedValue });
      setDirection('PAY_TO_BEN');
      setLastChange('SENDER');
      setLastChangedField('payment');

      // Validate payment_value first
      try {
        await form.validateFields(['payment_value']);

        // If payment_value is valid, calculate beneficiary_value
        if (beneficiaryCurrencyId && exchangeRate) {
          const beneficiaryCurrency = allCurrencies.find((item: any) => item.id === beneficiaryCurrencyId);
          if (beneficiaryCurrency) {
            const calculatedBeneficiaryValue = (val * exchangeRate).toFixed(beneficiaryDecimal);
            const calculatedBeneficiaryValueNum = Number(calculatedBeneficiaryValue);

            // Check max limit for beneficiary currency
            const maxLimitBeneficiary = getMaxLimit(beneficiaryCurrencyId);

            preservedBeneficiaryErrorRef.current = null;
            setPreservedBeneficiaryError(null);

            if (maxLimitBeneficiary !== null && calculatedBeneficiaryValueNum > maxLimitBeneficiary) {
              form.setFieldsValue({
                beneficiary_value: calculatedBeneficiaryValue,
              });
              setBeneficiaryAmount(calculatedBeneficiaryValueNum);
              const performValidation = async () => {
                try {
                  await form.validateFields(['beneficiary_value']);
                } catch {
                  // Validation errors are expected - ignore
                }
              };
              setTimeout(() => performValidation(), 0);
              setTimeout(() => performValidation(), 200);
              setTimeout(() => performValidation(), 500);
            } else {
              form.setFieldsValue({
                beneficiary_value: calculatedBeneficiaryValue,
              });
              setBeneficiaryAmount(calculatedBeneficiaryValueNum);

              const performValidation = async () => {
                try {
                  await form.validateFields(['beneficiary_value']);
                } catch {
                  // Validation errors are expected - ignore
                }
              };

              setTimeout(() => performValidation(), 0);
              setTimeout(() => performValidation(), 200);
              setTimeout(() => performValidation(), 500);
            }
          }
        }
      } catch (error) {
        // If payment_value has error, don't calculate beneficiary_value
        form.resetFields(['beneficiary_value']);
        form.setFields([{ name: 'beneficiary_value', errors: [] }]);
        setBeneficiaryAmount(0);
      }
    } else {
      form.resetFields(['beneficiary_value']);
      setPaymentAmount(0);
      setBeneficiaryAmount(0);
    }
  }, [form, paymentDecimal, beneficiaryDecimal, beneficiaryCurrencyId, exchangeRate, allCurrencies, getMaxLimit]);

  // Disable form fields until customer is selected
  // Customer selection is REQUIRED before any other fields can be filled
  const isFormDisabled = !customerData;

  useEffect(() => {
    if (!ethPrice && paymentType === PaymentTypes.EWallet) {
      (async () => {
        try {
          const priceResponse = await getETHPrice();
          if (priceResponse.status === 200) {
            setETHPrice(priceResponse.data);
          }
        } catch (error) {
          console.error('Error fetching ETH price:', error);
        }
      })();
    }
  }, [paymentType, ethPrice]);

  const calculateMintFee = useCallback(
    async (value: string) => {
      if (userWallet) {
        const contract = await getPayTokenContract(userWallet);
        const cryptoWallet = form.getFieldValue('cryptoWallet');
        if (contract && cryptoWallet) {
          const fee = await estimateMintGas(contract, cryptoWallet, value);
          if (fee) {
            setFee(formatEther(fee));
          }
        }
      }
    },
    [userWallet, form],
  );

  useEffect(() => {
    if (cryptoWallet && userWallet) {
      if (amount) {
        if (amount > 0) {
          calculateMintFee(amount.toString());
        } else {
          setFee('');
        }
      } else {
        setFee('');
      }
    }
  }, [userWallet, amount, cryptoWallet, calculateMintFee]);

  const mint = useCallback(async () => {
    const formData = form?.getFieldsValue();
    if (payTokenContract && userWallet && formData.cryptoWallet && formData?.amount) {
      try {
        await mintTokens(payTokenContract, formData.cryptoWallet, formData.amount.toString());
      } catch (e: any) {
        if (e?.info?.error?.code === -32000 || e?.info?.error?.data?.code === -32000) {
          api['error']({
            message: 'Transaction error',
            description: 'There is not enough funds on your balance',
          });
        } else if (e?.info?.error?.code === 4001 || e?.info?.error?.data?.code === 4001) {
          api['warning']({
            message: 'Operation cancelled',
            description: 'Transaction was cancelled by user',
          });
        } else {
          api['error']({
            message: 'Transaction error',
            description: 'Something went wrong',
          });
        }
        throw e;
      }
    } else {
      api['error']({
        message: 'Transaction error',
        description: 'Something went wrong',
      });
      return Promise.reject();
    }
  }, [form, userWallet, payTokenContract, api]);

  async function submitPayment() {
    if (paymentConfirmed.current && paymentRejected.current) {
      try {
        await mint();
        setPaymentConfirmationModalOpened(false);
        paymentConfirmed.current(null);
      } catch (e) {
        console.error(e);
        setPaymentConfirmationModalOpened(false);
        paymentRejected.current();
      }
    }
  }

  const clearCustomerData = useCallback(() => {
    setCustomerData(undefined);
    form.setFieldValue('customerId', undefined);
    form.setFieldValue('dateOfTransaction', dayjs());
    // Clear all form fields when customer is cleared
    // This ensures a clean state when customer is removed
    form.resetFields([
      'beneficiary',
      'beneficiary_value',
      'beneficiary_currency',
      'beneficiary_wallet_address',
      'payment_value',
      'payment_currency',
      'companyId',
      'bankId',
      'bankAccountId',
      'specialInstructions',
      'purposeOfRemittance',
      'status',
      'documents',
      'cryptoWallet',
      'amount',
      'currencyCode',
      'providerBankAccount',
      'providerWallet',
      'serviceProviderId',
      'smartUID',
      'providerId',
      'providerCurrencyId',
      'providerCurrency',
      'providerCurrencyCode',
      'providerCurrencySymbol',
      'providerCurrencyName',
      'providerCurrencyDecimal',
      'providerCurrencyRate',
      'providerCurrencyRate',
      'beneficiary_bank_account',
    ]);
    // Reset related state
    setCurrencyData(undefined);
    setPaymentCurrencyData(undefined);
    setCompanyData(undefined);
    setBankData(undefined);
    setAccountData(undefined);
    setWalletData(undefined);
    setServiceProviderData(undefined);
    setBeneficiaryCurrencyId(undefined);
    setPaymentCurrencyId(undefined);
    setHasSetDefault(false);
    setFileList([]);
    setProviderBankAccountData(undefined);
    setProviderWalletData(undefined);
  }, [form]);

  const changeCustomerData = useCallback(
    async (data: ICustomer) => {
      clearCustomerData()
      setCustomerData(data);
      form.setFieldValue('customerId', data.fullName);

      // If customer is selected and currencies are loaded, set default currencies and values
      if (data.id && allCurrencies?.length) {

        // Find currencies directly by code - check if they have id property
        const beneficiaryCurrencyDefault = allCurrencies.find((item: any) => (item.code === 'USD' || item.symbol === 'USD') && item.id);
        const paymentCurrencyDefault = allCurrencies.find((item: any) => (item.code === 'EUR' || item.symbol === 'EUR') && item.id);

        // If not found with id, try without id check (fallback)
        const beneficiaryCurrencyFallback = beneficiaryCurrencyDefault || allCurrencies.find((item: any) => item.code === 'USD' || item.symbol === 'USD');
        const paymentCurrencyFallback = paymentCurrencyDefault || allCurrencies.find((item: any) => item.code === 'EUR' || item.symbol === 'EUR');

        if (!beneficiaryCurrencyFallback || !paymentCurrencyFallback) {
          console.log('[FLOW] changeCustomerData - USD or EUR currency not found');
          return;
        }

        // Get IDs - try to get id from currency object
        // If currency object doesn't have id, we need to find it in allCurrencies by matching code
        let beneficiaryCurrencyId = beneficiaryCurrencyFallback.id;
        let paymentCurrencyId = paymentCurrencyFallback.id;

        // If no id in the found currency, search allCurrencies for one with matching code and id
        if (!beneficiaryCurrencyId) {
          const currencyWithId = allCurrencies.find((item: any) =>
            item.id && (item.code === 'USD' || item.symbol === 'USD')
          );
          if (currencyWithId) {
            beneficiaryCurrencyId = currencyWithId.id;
          }
        }

        if (!paymentCurrencyId) {
          const currencyWithId = allCurrencies.find((item: any) =>
            item.id && (item.code === 'EUR' || item.symbol === 'EUR')
          );
          if (currencyWithId) {
            paymentCurrencyId = currencyWithId.id;
          }
        }

        if (!beneficiaryCurrencyId || !paymentCurrencyId) {
          console.error('[FLOW] changeCustomerData - Currency IDs are missing! Cannot proceed.');
          return;
        }

        // Set currencies in form and state FIRST
        // Use symbol for form field value to match mapCurrencyItemWithIcon
        const beneficiarySymbol = beneficiaryCurrencyFallback.symbol || beneficiaryCurrencyFallback.code;
        const paymentSymbol = paymentCurrencyFallback.symbol || paymentCurrencyFallback.code;
        form.setFieldsValue({
          beneficiary_currency: beneficiarySymbol,
          payment_currency: paymentSymbol,
        });
        setBeneficiaryCurrencyData(paymentCurrencyFallback)
        // Set all currency states immediately - use fallback objects
        setCurrencyData(beneficiaryCurrencyFallback);
        setPaymentCurrencyData(paymentCurrencyFallback);
        setBeneficiaryCurrencyId(beneficiaryCurrencyId);
        setPaymentCurrencyId(paymentCurrencyId);
        setSendCurrency(beneficiaryCurrencyId);
        setPaymentCurrency(paymentCurrencyId);
        setSendCurrencySymbol(beneficiaryCurrencyFallback.symbol || beneficiaryCurrencyFallback.code);
        setPaymentCurrencySymbol(paymentCurrencyFallback.symbol || paymentCurrencyFallback.code);
        setBeneficiaryDecimal(beneficiaryCurrencyFallback.decimals || 2);
        setPaymentDecimal(paymentCurrencyFallback.decimals || 2);
        // Verify currencies are set before calling API
        if (!beneficiaryCurrencyId || !paymentCurrencyId) {
          console.log('[FLOW] changeCustomerData - Currencies not set, skipping API call');
          return;
        }

        // Fetch exchange rate (from EUR to USD) - only call when both currencies have values
        try {
          const rateResult = await getExchangeRate({ from: paymentCurrencyId, to: beneficiaryCurrencyId });
          const newRate = rateResult?.data?.data?.data || rateResult?.data?.data || rateResult?.data || 1;

          if (newRate && newRate > 0) {
            setExchangeRate(newRate);

            // Set default beneficiary value = 0
            const defaultBeneficiaryValue = '0.00';
            // // Set default beneficiary value = 1000
            // const defaultBeneficiaryValue = '1000.00';
            const defaultBeneficiaryValueNum = Number(defaultBeneficiaryValue);

            // Calculate payment value = 1000 / rate
            const calculatedPayment = (defaultBeneficiaryValueNum / newRate).toFixed(paymentCurrencyFallback.decimals || 2);
            const calculatedPaymentNum = Number(calculatedPayment);

            // Mark that we're setting default values
            isSettingDefaultRef.current = true;

            form.setFieldsValue({
              beneficiary_value: defaultBeneficiaryValue,
              payment_value: calculatedPayment,
            });
            setPaymentAmount(calculatedPaymentNum);
            setBeneficiaryAmount(defaultBeneficiaryValueNum);
            setLastChange('BENEFICIARY');
            setLastChangedField('beneficiary');
            setDirection('BEN_TO_PAY');

            // Reset flag after a short delay
            setTimeout(() => {
              isSettingDefaultRef.current = false;
            }, 0);

          } else {
            setExchangeRate(1);

            // Set default values with rate = 1
            const defaultBeneficiaryValue = '0.00';
            // // Set default values with rate = 1
            // const defaultBeneficiaryValue = '1000.00';
            const defaultBeneficiaryValueNum = Number(defaultBeneficiaryValue);
            const calculatedPayment = defaultBeneficiaryValueNum.toFixed(paymentCurrencyFallback.decimals || 2);

            isSettingDefaultRef.current = true;
            form.setFieldsValue({
              beneficiary_value: defaultBeneficiaryValue,
              payment_value: calculatedPayment,
            });
            setPaymentAmount(Number(calculatedPayment));
            setBeneficiaryAmount(defaultBeneficiaryValueNum);
            setLastChange('BENEFICIARY');
            setLastChangedField('beneficiary');
            setDirection('BEN_TO_PAY');

            setTimeout(() => {
              isSettingDefaultRef.current = false;
            }, 0);
          }
        } catch (error) {
          console.error('[FLOW] changeCustomerData - Error fetching exchange rate:', error);
          // Use rate = 1 as fallback
          setExchangeRate(1);

          const defaultBeneficiaryValue = '0.00';
          // const defaultBeneficiaryValue = '1000.00';
          const defaultBeneficiaryValueNum = Number(defaultBeneficiaryValue);
          const calculatedPayment = defaultBeneficiaryValueNum.toFixed(paymentCurrencyDefault.decimals || 2);

          isSettingDefaultRef.current = true;
          form.setFieldsValue({
            beneficiary_value: defaultBeneficiaryValue,
            payment_value: calculatedPayment,
          });
          setPaymentAmount(Number(calculatedPayment));
          setBeneficiaryAmount(defaultBeneficiaryValueNum);
          setLastChange('BENEFICIARY');
          setLastChangedField('beneficiary');
          setDirection('BEN_TO_PAY');

          setTimeout(() => {
            isSettingDefaultRef.current = false;
          }, 0);
        }
      }
    },
    [form, allCurrencies, clearCustomerData],
  );

  const handleFormValuesChange = useCallback((changedValues: any, allValues: any) => {
    const fields = Object.keys(allValues).filter(key => key !== 'dateOfTransaction');
    const filledFields = fields.filter(
      key => allValues[key] !== null && allValues[key] !== undefined && allValues[key] !== '',
    );
    setIsFormEmpty(filledFields.length === 0);
  }, []);

  useEffect(() => {
    form.setFieldValue('dateOfTransaction', dayjs());
    form.setFieldValue('status', PaymentStatus.DRAFT);
  }, [form]);

  // Set default currencies (USD for beneficiary, EUR for payment) - matching sample logic
  useEffect(() => {
    if (!allCurrencies?.length) {
      return;
    }

    const beneficiaryCurrencyDefault = findCurrencyBySymbol('USD');
    const paymentCurrencyDefault = findCurrencyBySymbol('EUR');

    // Only proceed if we found the currencies (avoid hardcoded fallback IDs)
    if (!beneficiaryCurrencyDefault || !paymentCurrencyDefault) return;

    const beneficiaryCurrencyId = beneficiaryCurrencyDefault.id;
    const paymentCurrencyId = paymentCurrencyDefault.id;

    // Set default currency values if not already set
    const currentBeneficiaryCurrency = form.getFieldValue('beneficiary_currency');
    const currentPaymentCurrency = form.getFieldValue('payment_currency');

    if (!currentBeneficiaryCurrency) {
      // Find currency by code (USD) and set its symbol as default
      const beneficiarySymbol = beneficiaryCurrencyDefault.symbol || beneficiaryCurrencyDefault.code;
      form.setFieldsValue({
        beneficiary_currency: beneficiarySymbol,
      });
      setBeneficiaryCurrencyData(beneficiaryCurrencyDefault)
      setCurrencyData(beneficiaryCurrencyDefault);
      setBeneficiaryCurrencyId(beneficiaryCurrencyId);
      setSendCurrency(beneficiaryCurrencyId);
      setSendCurrencySymbol(beneficiaryCurrencyDefault.symbol || beneficiaryCurrencyDefault.code);
      setBeneficiaryDecimal(beneficiaryCurrencyDefault.decimals || 2);
    }

    if (!currentPaymentCurrency) {
      // Find currency by code (EUR) and set its symbol as default
      const paymentSymbol = paymentCurrencyDefault.symbol || paymentCurrencyDefault.code;
      form.setFieldsValue({
        payment_currency: paymentSymbol,
      });
      setPaymentCurrencyData(paymentCurrencyDefault);
      setPaymentCurrencyId(paymentCurrencyId);
      setPaymentCurrency(paymentCurrencyId);
      setPaymentCurrencySymbol(paymentCurrencyDefault.symbol || paymentCurrencyDefault.code);
      setPaymentDecimal(paymentCurrencyDefault.decimals || 2);
    }
  }, [allCurrencies, form, findCurrencyBySymbol]);

  useEffect(() => {
    if (!sendCurrency) return
    fetchConfigurationFee(sendCurrency)

    const intervalId = setInterval(() => {
      fetchConfigurationFee(sendCurrency)
    }, 60000)
    return () => clearInterval(intervalId)
  }, [sendCurrency, fetchConfigurationFee])

  // Set default values when all required data is loaded - matching sample logic
  useEffect(() => {
    if (!allCurrencies?.length) return;
    if (!paymentDecimal || paymentDecimal === 0) return;
    if (!beneficiaryDecimal || beneficiaryDecimal === 0) return;
    if (hasSetDefaultRef.current) {
      return; // Only set default once
    }

    const beneficiaryCurrencyDefault = findCurrencyBySymbol('USD');
    const paymentCurrencyDefault = findCurrencyBySymbol('EUR');

    if (!beneficiaryCurrencyDefault || !paymentCurrencyDefault) return;

    const beneficiaryCurrencyId = beneficiaryCurrencyDefault.id;
    const paymentCurrencyId = paymentCurrencyDefault.id;

    // Check if currencies are already set correctly
    const currentBeneficiaryCurrency = form.getFieldValue('beneficiary_currency');
    const currentPaymentCurrency = form.getFieldValue('payment_currency');

    // If currencies don't match defaults, don't set default values yet
    if (currentBeneficiaryCurrency && currentBeneficiaryCurrency !== beneficiaryCurrencyId) {
      hasSetDefaultRef.current = true;
      return;
    }
    if (currentPaymentCurrency && currentPaymentCurrency !== paymentCurrencyId) {
      hasSetDefaultRef.current = true;
      return;
    }

    // Check if user has already entered values manually
    const currentBeneficiaryValue = form.getFieldValue('beneficiary_value');
    const currentPaymentValue = form.getFieldValue('payment_value');
    const beneficiaryValueNum = Number(currentBeneficiaryValue) || 0;
    const paymentValueNum = Number(currentPaymentValue) || 0;

    // If user has already entered values, don't set default
    if (beneficiaryValueNum > 0 || paymentValueNum > 0) {
      hasSetDefaultRef.current = true;
      return;
    }

    // Set default beneficiary value to 1000 (as per requirement)
    const defaultBeneficiaryValue = '0.00';
    // // Set default beneficiary value to 1000 (as per requirement)
    // const defaultBeneficiaryValue = '1000.00';
    // Use rate from rateData if available, otherwise use current exchangeRate (defaults to 1)
    // Wait for rate to be available before setting default values
    const currentRate = rateData?.data?.data || rateData?.data || exchangeRate || 1;

    // Calculate payment value from beneficiary value and rate
    const paymentValue = Number((Number(defaultBeneficiaryValue) * (1 / currentRate)).toFixed(paymentDecimal));

    // Set direction and lastChange to prevent recalculation in other useEffect
    setDirection('BEN_TO_PAY');
    setLastChange('BENEFICIARY');
    setLastChangedField('beneficiary');

    // Mark that we're setting default values
    isSettingDefaultRef.current = true;

    form.setFieldsValue({
      beneficiary_value: defaultBeneficiaryValue,
      payment_value: paymentValue,
    });
    setPaymentAmount(paymentValue);
    setBeneficiaryAmount(Number(defaultBeneficiaryValue));

    // Mark that default has been set and reset isSettingDefault flag
    hasSetDefaultRef.current = true;
    setTimeout(() => {
      isSettingDefaultRef.current = false;
    }, 0);
  }, [allCurrencies, rateData?.data?.data, rateData?.data, paymentDecimal, beneficiaryDecimal, exchangeRate, form, findCurrencyBySymbol]);
  // Calculate fees based on config fee
  useEffect(() => {
    if (!configFee || loadingFee || paymentType !== PaymentTypes.Fiat) {
      return;
    }
    if (isChangingCurrency) {
      return;
    }

    const beneficiaryAmount = Number(beneficiaryValue) || 0;
    const isCrypto = allCurrencies.some(
      (item: any) => item.id === beneficiaryCurrencyId && item.type === "CRYPTO"
    );

    // Calculate fees based on who pays (SENDER or BENEFICIARY)
    if (feesPayer === "SENDER") {
      // Sender pays fee - fees are in payment currency
      // Calculate service fee
      switch (configFee?.serviceFeeType) {
        case "PERCENTAGE": {
          if (!beneficiaryAmount) {
            setServiceFee(null);
          } else {
            const serviceFeeValue = Number(
              roundUp(
                ((beneficiaryAmount || 0) * configFee?.serviceFee) /
                100 /
                exchangeRate,
                paymentDecimal
              )
            );
            setServiceFee(serviceFeeValue);
          }
          break;
        }

        case "FIXED": {
          const serviceFeeValue = Number(
            roundUp(
              (configFee?.serviceFee * configFee?.usdToFeeCurrencyRate) /
              exchangeRate,
              paymentDecimal
            )
          );
          setServiceFee(serviceFeeValue);
          break;
        }
        default:
          setServiceFee(0);
      }

      // Calculate transfer fee
      switch (
      isCrypto
        ? configFee?.cryptoTransferFeeType
        : configFee?.fiatTransferFeeType
      ) {
        case "PERCENTAGE": {
          if (!beneficiaryAmount) {
            setTransferFee(null);
          } else {
            setTransferFee(
              Number(
                roundUp(
                  (Number(beneficiaryAmount || 0) * configFee?.transferFee) /
                  100 /
                  exchangeRate,
                  paymentDecimal
                )
              )
            );
          }
          break;
        }
        case "FIXED":
          setTransferFee(
            Number(
              roundUp(
                (configFee?.transferFee * configFee?.usdToFeeCurrencyRate) /
                exchangeRate,
                paymentDecimal
              )
            )
          );
          break;
        case "DYNAMIC": {
          if (!walletData) {
            setFeeConfigId(undefined);
            setTransferFee(null);
          } else {
            const feeConfig = configFee?.transferFees?.find(
              (item: any) => item.networkId === walletData?.network?.id
            );
            setFeeConfigId(feeConfig?.id);
            setTransferFee(
              Number(
                roundUp((feeConfig?.fee || 0) / exchangeRate, paymentDecimal)
              )
            );
          }
          break;
        }
        default:
          setTransferFee(0);
      }
    } else {
      // Beneficiary pays fee - fees are in beneficiary currency
      // Calculate service fee
      switch (configFee?.serviceFeeType) {
        case "PERCENTAGE": {
          if (!beneficiaryAmount) {
            setServiceFee(null);
          } else {
            setServiceFee(
              Number(
                roundUp(
                  ((beneficiaryAmount || 0) * configFee?.serviceFee) / 100,
                  beneficiaryDecimal
                )
              )
            );
          }
          break;
        }
        case "FIXED":
          setServiceFee(
            Number(
              roundUp(
                configFee?.serviceFee * configFee?.usdToFeeCurrencyRate,
                beneficiaryDecimal
              )
            )
          );
          break;
        default:
          setServiceFee(0);
      }

      // Calculate transfer fee
      switch (
      isCrypto
        ? configFee?.cryptoTransferFeeType
        : configFee?.fiatTransferFeeType
      ) {
        case "PERCENTAGE": {
          if (!beneficiaryAmount) {
            setTransferFee(null);
          } else {
            setTransferFee(
              Number(
                roundUp(
                  (Number(beneficiaryAmount || 0) * configFee?.transferFee) /
                  100,
                  beneficiaryDecimal
                )
              )
            );
          }
          break;
        }
        case "FIXED":
          setTransferFee(
            Number(
              roundUp(
                configFee?.transferFee * configFee?.usdToFeeCurrencyRate,
                beneficiaryDecimal
              )
            )
          );
          break;
        case "DYNAMIC": {
          if (!walletData) {
            setFeeConfigId(undefined);
            setTransferFee(null);
          } else {
            const feeConfig = configFee?.transferFees?.find(
              (item: any) => item.networkId === walletData?.network?.id
            );
            setFeeConfigId(feeConfig?.id);
            setTransferFee(
              Number(roundUp(feeConfig?.fee || 0, beneficiaryDecimal))
            );
          }
          break;
        }
        default:
          setTransferFee(0);
      }
    }
  }, [
    configFee,
    beneficiaryValue,
    exchangeRate,
    loadingFee,
    beneficiaryCurrencyId,
    paymentCurrencyId,
    paymentType,
    allCurrencies,
    paymentDecimal,
    beneficiaryDecimal,
    feesPayer,
    isChangingCurrency,
    roundUp,
    walletData,
  ]);

  // Force recalculate fees when feesPayer changes
  // This useEffect ensures fees are recalculated immediately when feesPayer changes
  // even if the main useEffect is blocked by other conditions
  useEffect(() => {
    // Only trigger if we have all required data
    if (!configFee || loadingFee || paymentType !== PaymentTypes.Fiat || isChangingCurrency) {
      return;
    }

    const beneficiaryAmount = Number(beneficiaryValue) || 0;
    const isCrypto = allCurrencies.some(
      (item: any) => item.id === beneficiaryCurrencyId && item.type === "CRYPTO"
    );

    // Calculate fees based on who pays (SENDER or BENEFICIARY)
    if (feesPayer === "SENDER") {
      // Sender pays fee - fees are in payment currency
      // Calculate service fee
      switch (configFee?.serviceFeeType) {
        case "PERCENTAGE": {
          if (!beneficiaryAmount) {
            setServiceFee(null);
          } else {
            const serviceFeeValue = Number(
              roundUp(
                ((beneficiaryAmount || 0) * configFee?.serviceFee) /
                100 /
                exchangeRate,
                paymentDecimal
              )
            );
            setServiceFee(serviceFeeValue);
          }
          break;
        }
        case "FIXED": {
          const serviceFeeValue = Number(
            roundUp(
              (configFee?.serviceFee * configFee?.usdToFeeCurrencyRate) /
              exchangeRate,
              paymentDecimal
            )
          );
          setServiceFee(serviceFeeValue);
          break;
        }
        default:
          setServiceFee(0);
      }

      // Calculate transfer fee
      switch (
      isCrypto
        ? configFee?.cryptoTransferFeeType
        : configFee?.fiatTransferFeeType
      ) {
        case "PERCENTAGE": {
          if (!beneficiaryAmount) {
            setTransferFee(null);
          } else {
            setTransferFee(
              Number(
                roundUp(
                  (Number(beneficiaryAmount || 0) * configFee?.transferFee) /
                  100 /
                  exchangeRate,
                  paymentDecimal
                )
              )
            );
          }
          break;
        }
        case "FIXED":
          setTransferFee(
            Number(
              roundUp(
                (configFee?.transferFee * configFee?.usdToFeeCurrencyRate) /
                exchangeRate,
                paymentDecimal
              )
            )
          );
          break;
        case "DYNAMIC": {
          if (!walletData) {
            setFeeConfigId(undefined);
            setTransferFee(null);
          } else {
            const feeConfig = configFee?.transferFees?.find(
              (item: any) => item.networkId === walletData?.network?.id
            );
            setFeeConfigId(feeConfig?.id);
            setTransferFee(
              Number(
                roundUp((feeConfig?.fee || 0) / exchangeRate, paymentDecimal)
              )
            );
          }
          break;
        }
        default:
          setTransferFee(0);
      }
    } else {
      // Beneficiary pays fee - fees are in beneficiary currency
      // Calculate service fee
      switch (configFee?.serviceFeeType) {
        case "PERCENTAGE": {
          if (!beneficiaryAmount) {
            setServiceFee(null);
          } else {
            setServiceFee(
              Number(
                roundUp(
                  ((beneficiaryAmount || 0) * configFee?.serviceFee) / 100,
                  beneficiaryDecimal
                )
              )
            );
          }
          break;
        }
        case "FIXED":
          setServiceFee(
            Number(
              roundUp(
                configFee?.serviceFee * configFee?.usdToFeeCurrencyRate,
                beneficiaryDecimal
              )
            )
          );
          break;
        default:
          setServiceFee(0);
      }

      // Calculate transfer fee
      switch (
      isCrypto
        ? configFee?.cryptoTransferFeeType
        : configFee?.fiatTransferFeeType
      ) {
        case "PERCENTAGE": {
          if (!beneficiaryAmount) {
            setTransferFee(null);
          } else {
            setTransferFee(
              Number(
                roundUp(
                  (Number(beneficiaryAmount || 0) * configFee?.transferFee) /
                  100,
                  beneficiaryDecimal
                )
              )
            );
          }
          break;
        }
        case "FIXED":
          setTransferFee(
            Number(
              roundUp(
                configFee?.transferFee * configFee?.usdToFeeCurrencyRate,
                beneficiaryDecimal
              )
            )
          );
          break;
        case "DYNAMIC": {
          if (!walletData) {
            setFeeConfigId(undefined);
            setTransferFee(null);
          } else {
            const feeConfig = configFee?.transferFees?.find(
              (item: any) => item.networkId === walletData?.network?.id
            );
            setFeeConfigId(feeConfig?.id);
            setTransferFee(
              Number(roundUp(feeConfig?.fee || 0, beneficiaryDecimal))
            );
          }
          break;
        }
        default:
          setTransferFee(0);
      }
    }
  }, [
    feesPayer,
    configFee,
    beneficiaryValue,
    exchangeRate,
    loadingFee,
    paymentType,
    isChangingCurrency,
    allCurrencies,
    beneficiaryCurrencyId,
    paymentDecimal,
    beneficiaryDecimal,
    roundUp,
    walletData,
  ]); // Depend on feesPayer and all required data to force recalculation when feesPayer changes

  // Re-validate beneficiary_value after fee is fetched to ensure validation uses correct fee data
  useEffect(() => {
    if (!configFee || loadingFee || isChangingCurrency) {
      return;
    }

    // If beneficiary pays fee, wait for transferFee and serviceFee to be calculated
    if (feesPayer === 'BENEFICIARY') {
      if (transferFee === null || serviceFee === null) {
        return;
      }
    }

    const beneficiaryValue = form.getFieldValue('beneficiary_value');

    if (beneficiaryValue && Number(beneficiaryValue) > 0) {
      // Use setTimeout to ensure state is updated and avoid conflicts with other validations
      setTimeout(() => {
        if (!isChangingCurrency) {
          form.validateFields(['beneficiary_value']).then(() => {
            // Clear preserved error since validation passed with new fees
            setPreservedBeneficiaryError(null);
          }).catch(() => {
            // Error is expected if value is invalid, just ensure validation runs
          });
        }
      }, 200);
    }
  }, [configFee, loadingFee, paymentCurrencyId, beneficiaryCurrencyId, transferFee, serviceFee, feesPayer, form, isChangingCurrency]);

  // Check balance and update disabledPortfolio state
  useEffect(() => {
    if (!avlBalance || !paymentCurrencyId) {
      setDisabledPortfolio(true);
      return;
    }

    const totalBalance = avlBalance?.data?.data?.totalBalance ?? 0;
    if (totalBalance === 0) {
      setPaymentMethod(listPaymentMethod.DIRECTER_TRANSFER);
      setDisabledPortfolio(true);
      return;
    }

    if (transferFee === null || serviceFee === null) return;

    if (isFirstLoadBalance) {
      if (feesPayer === 'SENDER') {
        if (
          totalBalance >=
          Number(
            (paymentAmount + transferFee + serviceFee).toFixed(paymentDecimal)
          )
        ) {
          setPaymentMethod(listPaymentMethod.PORTFOLIO);
          setDisabledPortfolio(false);
        } else {
          setPaymentMethod(listPaymentMethod.DIRECTER_TRANSFER);
          setDisabledPortfolio(true);
        }
      } else {
        if (totalBalance >= Number(paymentAmount.toFixed(paymentDecimal))) {
          setPaymentMethod(listPaymentMethod.PORTFOLIO);
          setDisabledPortfolio(false);
        } else {
          setPaymentMethod(listPaymentMethod.DIRECTER_TRANSFER);
          setDisabledPortfolio(true);
        }
      }
    } else {
      if (feesPayer === 'SENDER') {
        if (
          totalBalance <
          Number(
            (paymentAmount + transferFee + serviceFee).toFixed(paymentDecimal)
          )
        ) {
          setPaymentMethod(listPaymentMethod.DIRECTER_TRANSFER);
          setDisabledPortfolio(true);
        } else {
          setDisabledPortfolio(false);
        }
      } else {
        if (totalBalance < Number(paymentAmount.toFixed(paymentDecimal))) {
          setPaymentMethod(listPaymentMethod.DIRECTER_TRANSFER);
          setDisabledPortfolio(true);
        } else {
          setDisabledPortfolio(false);
        }
      }
    }
  }, [
    avlBalance,
    paymentAmount,
    transferFee,
    serviceFee,
    feesPayer,
    isFirstLoadBalance,
    paymentCurrencyId,
    paymentDecimal,
  ]);

  // Sync fileList with form value
  useEffect(() => {
    const formDocuments = form.getFieldValue('documents');
    if (formDocuments && Array.isArray(formDocuments)) {
      setFileList(formDocuments);
    }
  }, [form]);

  const changeCompanyData = useCallback(
    (data: ICompany) => {
      setIsBeneficiaryLoading(true)
      setPaymentType(PaymentTypes.Fiat);
      if (data?.id) {
        setBeneficiaryCompanyId(data?.id);
      } else {
        setBeneficiaryCompanyId(undefined);
      }
      if (data.id !== companyData?.id || data.id === undefined) {
        setCompanyData(data);
        form.setFieldValue('companyId', data.name);
        setAccountData(undefined);
        setBankData(undefined);
        setWalletData(undefined);
        // Reset wallets and bankAccounts when company changes to avoid using stale data
        setWallets(undefined);
        setBankAccounts(undefined);
        setIsWalletLoading(false);
        setIsBankLoading(false);
        form.validateFields(['companyId']);
        form.setFieldValue('bankId', undefined);
        form.setFieldValue('bankAccountId', undefined);
        form.setFieldValue('beneficiary_wallet_address', undefined);
        form.setFieldValue('beneficiary_bank_account', undefined);
      }
    },
    [companyData?.id, form, setIsBeneficiaryLoading],
  );

  const onSelectCustomer = useCallback(
    (_: ICustomer, { data }: Option<ICustomer>) => {
      changeCustomerData(data);
    },
    [changeCustomerData],
  );

  const onSelectCompany = useCallback(
    (_: ICompany, { data }: Option<ICompany>) => {
      changeCompanyData(data);
    },
    [changeCompanyData],
  );

  const onSelectServiceProvider = useCallback((_: ICompany, { data }: Option<ICompany>) => {
    setServiceProviderData(data);
    // Reset provider bank account and wallet when provider changes
    setProviderBankAccountData(undefined);
    setProviderWalletData(undefined);
    form.setFieldValue('serviceProviderId', data?.name);
    form.setFieldValue('providerBankAccount', undefined);
    form.setFieldValue('providerWallet', undefined);
  }, [form]);

  const onSelectProviderBankAccount = useCallback((_: any, { data }: Option<any>) => {
    setProviderBankAccountData(data);
    form.setFieldValue('providerBankAccount', data?.accountNumber);
  }, [form]);

  const clearProviderBankAccountData = useCallback(() => {
    setProviderBankAccountData(undefined);
    form.setFieldValue('providerBankAccount', undefined);
  }, [form]);

  const onSelectProviderWallet = useCallback((_: any, { data }: Option<any>) => {
    setProviderWalletData(data);
    form.setFieldValue('providerWallet', data?.address);
  }, [form]);

  const clearProviderWalletData = useCallback(() => {
    setProviderWalletData(undefined);
    form.setFieldValue('providerWallet', undefined);
  }, [form]);

  const changeBankData = useCallback(
    (data: IBank) => {
      if (data.id !== bankData?.id || data.id === undefined) {
        setBankData(data);
        form.setFieldValue('bankId', data.name);
        setAccountData(undefined);
        form.setFieldValue('bankAccountId', '');
      }
    },
    [bankData?.id, form],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSelectBank = useCallback((_: IBank, { data }: Option<IBank>) => changeBankData(data), [changeBankData]);

  const changeBankAccountData = useCallback(
    (data: IBankAccount) => {
      setBankData(data.bank);
      setAccountData(data);
      const displayValue = buildBankAccountValue(data);
      form.setFieldValue('beneficiary_bank_account', displayValue);
      form.setFieldValue('bankId', data.bank?.name);
      form.setFieldValue('bankAccountId', displayValue);
      form.validateFields(['beneficiary_bank_account', 'bankId', 'bankAccountId']);
    },
    [form],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSelectBankAccount = useCallback(
    (_: IBankAccount, { data }: Option<IBankAccount>) => {
      changeBankAccountData(data);
    },
    [changeBankAccountData],
  );

  const clearCurrencyData = useCallback(() => {
    setCurrencyData(undefined);
  }, []);

  const clearPaymentCurrencyData = useCallback(() => {
    setPaymentCurrencyData(undefined);
  }, []);

  const clearCompanyData = useCallback(() => {
    setCompanyData(undefined);
    setAccountData(undefined);
    setBankData(undefined);
    setWalletData(undefined);
    setBeneficiaryCompanyId(undefined);
    form.setFieldValue('bankId', '');
    form.setFieldValue('bankAccountId', '');
    form.setFieldValue('beneficiary', undefined);
    form.setFieldValue('beneficiary_wallet_address', undefined);
    form.setFieldValue('beneficiary_bank_account', undefined);
  }, [form]);

  const clearBankData = useCallback(() => {
    setBankData(undefined);
    setAccountData(undefined);
    form.setFieldValue('bankAccountId', '');
  }, [form]);

  const changeWalletData = useCallback(
    (data: IWallet) => {
      setWalletData(data);
      const displayValue = buildWalletValue(data);
      form.setFieldValue('beneficiary_wallet_address', displayValue);
      form.validateFields(['beneficiary_wallet_address']);
    },
    [form],
  );

  const onSelectWallet = useCallback(
    (_: IWallet, { data }: Option<IWallet>) => {
      changeWalletData(data);
    },
    [changeWalletData],
  );

  const clearWalletData = useCallback(() => {
    setWalletData(undefined);
    form.setFieldValue('beneficiary_wallet_address', '');
  }, [form]);

  const clearBankAccountData = useCallback(() => {
    setAccountData(undefined);
  }, []);

  const clearServiceProviderData = useCallback(() => {
    setServiceProviderData(undefined);
  }, []);

  const onSubmit = useCallback(
    async (formValues: IPaymentFormValues) => {
      // Use new field names if available, otherwise fall back to old field names for backward compatibility
      const beneficiaryValue = (formValues as any).beneficiary_value ?? formValues.amount;
      const paymentValue = (formValues as any).payment_value ?? formValues.amount;

      // Determine transferMode dựa theo cách xử lý ở FormTransferV2
      // - Nếu là EWallet (crypto):
      //   + Mặc định: CRYPTO_NETWORK
      //   + Nếu beneficiary là SMART user (có SMART UID + infoByUserCode) và KHÔNG có ví crypto nào trong danh sách → INTERNAL_TRANSFER
      // - Nếu là Fiat:
      //   + Mặc định: BANK_TRANSFER
      //   + Nếu beneficiary là SMART user (có SMART UID + infoByUserCode) và KHÔNG có bank account nào trong danh sách → INTERNAL_TRANSFER
      const hasSmartUser =
        !!companyData?.refUser?.smartUID && !!infoByUserCode?.id;

      let transferMode: 'INTERNAL_TRANSFER' | 'BANK_TRANSFER' | 'CRYPTO_NETWORK';

      if (paymentType === PaymentTypes.EWallet) {
        const walletsCount = wallets?.length ?? 0;
        if (walletsCount <= 0 && hasSmartUser) {
          transferMode = 'INTERNAL_TRANSFER';
        } else {
          transferMode = 'CRYPTO_NETWORK';
        }
      } else {
        const bankAccountCount = bankAccounts?.length ?? 0;
        if (bankAccountCount <= 0 && hasSmartUser) {
          transferMode = 'INTERNAL_TRANSFER';
        } else {
          transferMode = 'BANK_TRANSFER';
        }
      }

      // Determine receiverMethod based on transferMode (matching code sample)
      // 'INTERNAL' if transferMode === INTERNAL_TRANSFER, 'EXTERNAL' otherwise
      const receiverMethod = transferMode === 'INTERNAL_TRANSFER' ? 'INTERNAL' : 'EXTERNAL';

      // Determine receiverUserId (matching code sample)
      // Only set if transferMode === INTERNAL_TRANSFER
      const receiverUserId = transferMode === 'INTERNAL_TRANSFER' ? infoByUserCode?.id : undefined;

      // For providerWalletId, providerBankAccountId, beneficiaryWalletId
      // Get from form values or states if available (matching code sample logic)
      // In code sample:
      // - providerWalletId: walletSelectedProvider?.id (from form field 'providerWallet' or state)
      // - providerBankAccountId: bankAccountSelectedProvider?.id (from form field 'providerBankAccount' or state)
      // - beneficiaryWalletId: walletSelected?.id (for CRYPTO_NETWORK mode, from form field 'beneficiary_wallet_address' or state)

      // Get providerWalletId from state (form field contains address string, not ID)
      // IMPORTANT: Form field 'providerWallet' contains address (string), not ID
      // Only use state to get ID, do NOT use form field value
      const providerWalletIdValue = providerWalletData?.id || undefined;

      // Get providerBankAccountId from state (form field contains accountNumber string, not ID)
      // IMPORTANT: Form field 'providerBankAccount' contains accountNumber (string), not ID
      // Only use state to get ID, do NOT use form field value
      const providerBankAccountIdValue = providerBankAccountData?.id || undefined;

      // Get beneficiaryWalletId from state (for CRYPTO_NETWORK mode)
      // IMPORTANT: Form field 'beneficiary_wallet_address' may contain display value, not ID
      // Only use state walletData?.id to get the actual ID
      const beneficiaryWalletIdValue = walletData?.id;

      const specialInstructions = formValues.specialInstructions;
      const specialInstructionsDetail =
        specialInstructions &&
          specialInstructions.startsWith(specialInstructionsDraft || '')
          ? specialInstructions
            .slice(specialInstructionsDraft?.length)
            .trim() ?? undefined
          : specialInstructions || undefined;

      // Extract documents as an array of file names and validate upload status
      let documents: string[] | undefined;
      if (formValues.documents && formValues.documents.length > 0) {
        const hasInvalidDocument = formValues.documents.some((file: any) => {
          return file?.status !== 'done' || !file?.response;
        });

        if (hasInvalidDocument) {
          openNotification?.({
            type: 'error',
            message: 'Documents are invalid',
          });
          return;
        }

        documents = formValues.documents
          .map((file: any) => file?.response)
          .filter((name: any) => typeof name === 'string' && name.trim().length > 0);
      }
      const data = {
        senderAmount: paymentValue ? Number(paymentValue) : 0,
        receiverAmount: beneficiaryValue ? Number(beneficiaryValue) : 0,
        direction,
        companyId: companyData?.id,
        bankAccountId: accountData?.id,
        receiveCurrencyId: beneficiaryCurrencyId,
        paymentCurrencyId,
        specialInstructions: specialInstructionsDraft ?? undefined,
        specialInstructionsDetail,
        documents,
        providerWalletId: providerWalletIdValue || undefined,
        senderMethod: 'INTERNAL',
        receiverMethod,
        feesPayer,
        feeConfigVersion: configFee?.version,
        dateOfTransaction: formValues.dateOfTransaction,
        serviceProviderId: serviceProviderData?.id,
        receiverUserId,
        providerBankAccountId: providerBankAccountIdValue || undefined,
        beneficiaryWalletId: beneficiaryWalletIdValue,
        feeConfigId,
        exchangeRate,
        usdToFeeCurrencyRate: configFee?.usdToFeeCurrencyRate || 1,
        purposeOfRemittance: formValues.purposeOfRemittance,
        status: "CREATED",
        // userId comes from the selected customer
        userId: customerData?.id,
        customerId: customerData?.id,
        paymentAmount,
      };

      createPaymentRequest(
        {
          resource: 'api/v1/payment_requests/create',
          values: data,
          errorNotification: false,
        },
        {
          onSuccess: (response: any) => {
            if (openNotification) {
              openNotification({
                type: 'success',
                message: 'Send money created successfully',
              });
            }
            const createdId = response?.data?.id || response?.data?.data?.id;
            if (createdId) {
              navigate(`/payment-request?editId=${createdId}`);
            } else {
              navigate('/payment-request');
            }
          },
          onError: (error: any) => {
            if (error?.reason_code === 'ERR00104') {
              setShowReviewFee(true)
            } else if (openNotification) {
              openNotification({
                type: 'error',
                message: 'Error creating send money',
                description: error?.message || 'Something went wrong',
              });
            }
          },
        }
      );
    },
    [
      customerData,
      serviceProviderData?.id,
      companyData,
      accountData,
      paymentType,
      direction,
      beneficiaryCurrencyId,
      paymentCurrencyId,
      exchangeRate,
      configFee,
      feeConfigId,
      feesPayer,
      infoByUserCode,
      createPaymentRequest,
      openNotification,
      navigate,
      providerBankAccountData?.id,
      providerWalletData?.id,
      walletData,
      paymentAmount,
      specialInstructionsDraft,
      wallets,
      bankAccounts,
    ],
  );

  const { openNotification: openDifferentCurrenciesNotification, contextHolder: differentCurrenciesContextHolder } =
    useNotificationOkCancel({
      ...confirmModalDifferentCurrenciesProps,
      onOk: () => {
        form.submit();
      },
    });

  const handleSubmit = useCallback(
    (buttonKey: 'save' | 'send') => async () => {
      hasInputBeneficiaryRef.current = true;
      hasInputPaymentRef.current = true;
      const nextStatus = isAdminRole
        ? form.getFieldValue('status')
        : buttonKey === 'save'
          ? PaymentStatus.DRAFT
          : PaymentStatus.PENDING;
      try {
        setNextStatus(nextStatus);
        if (
          accountData?.currencyCode !== undefined &&
          currencyData?.code !== undefined &&
          accountData?.currencyCode !== currencyData?.code
        ) {
          openDifferentCurrenciesNotification();
        } else {
          form.submit();
        }
      } catch (error) {
        form.submit();
      }
    },
    [isAdminRole, form, accountData?.currencyCode, currencyData?.code, openDifferentCurrenciesNotification],
  );

  const [companyCreateForm] = Form.useForm<ICompany>();
  const {
    close: createCompanyModalClose,
    modalProps: createCompanyModalProps,
    formProps: createCompanyFormProps,
    formLoading: createCompanyFormLoading,
    submit: createCompanyFormSubmit,
  } = useModalForm({
    autoSubmitClose: true,
    form: companyCreateForm,
    submit: async formValues => {
      const company = await createCompany(formValues).then(res => res.data);
      setCompanyData(company);
      createCompanyModalClose();
    },
  });

  const [bankCreateForm] = Form.useForm<IBank>();
  const {
    close: createBankModalClose,
    modalProps: createBankModalProps,
    formProps: createBankFormProps,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    show: createBankModalShow,
    formLoading: createBankFormLoading,
    submit: createBankFormSubmit,
  } = useModalForm({
    autoSubmitClose: true,
    form: bankCreateForm,
    submit: () => {
      createBankModalClose();
    },
  });

  const [bankAccountCreateForm] = Form.useForm<IBankAccount>();
  const {
    close: createBankAccountModalClose,
    modalProps: createBankAccountModalProps,
    formProps: createBankAccountFormProps,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    show: createBankAccountModalShow,
    formLoading: createBankAccountFormLoading,
    submit: createBankAccountFormSubmit,
  } = useModalForm({
    autoSubmitClose: true,
    form: bankAccountCreateForm,
    submit: () => {
      createBankAccountModalClose();
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const companyFilter = useMemo(
    (): CrudFilter => ({
      field: 'companyId',
      operator: 'eq',
      value: companyData?.id ?? -1,
    }),
    [companyData],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const bankFilter = useMemo(
    (): CrudFilter => ({
      field: 'bankId',
      operator: 'eq',
      value: bankData?.id ?? -1,
    }),
    [bankData],
  );

  const onUnsavedChangesOk = useCallback(() => {
    form.resetFields();
    clearCurrencyData();
    clearCompanyData();
    clearBankData();
    clearBankAccountData();
    navigate('/payment-request');
    setAmountData(null);
    setInstructionsData(undefined);
    setFileList([]);
  }, [clearBankAccountData, clearBankData, clearCompanyData, clearCurrencyData, navigate, form]);

  const { openNotification: openUnsavedChangesNotification, contextHolder: unsavedChangesContextHolder } =
    useNotificationOkCancel({
      ...confirmModalUnsavedChangesProps,
      onOk: onUnsavedChangesOk,
    });

  const handleCancel = useCallback(() => {
    const beneficiaryValue = form.getFieldValue('beneficiary_value');
    const paymentValue = form.getFieldValue('payment_value');
    const beneficiaryCurrency = form.getFieldValue('beneficiary_currency');
    const paymentCurrency = form.getFieldValue('payment_currency');
    const isFullFields = currencyCode || bankData || companyData || accountData || amountData || instructionsData ||
      beneficiaryValue || paymentValue || beneficiaryCurrency || paymentCurrency;
    if (isFullFields) {
      openUnsavedChangesNotification();
    } else {
      onUnsavedChangesOk();
    }
  }, [
    currencyCode,
    bankData,
    companyData,
    accountData,
    amountData,
    instructionsData,
    form,
    openUnsavedChangesNotification,
    onUnsavedChangesOk,
  ]);

  const onChangeUpload = useCallback((info: UploadChangeParam) => {
    const { file, fileList: newFileList } = info;

    // Update uploading status
    setIsUploading(file.status === 'uploading' || file.status === 'error');

    // Update file list
    setFileList(newFileList);

    // Validate file count (max 5 files)
    const MAX_FILE_COUNT = 5;
    if (newFileList.length > MAX_FILE_COUNT) {
      api['error']({
        message: 'File limit exceeded',
        description: `You can upload up to ${MAX_FILE_COUNT} files only!`,
      });
      // Remove excess files
      const validFileList = newFileList.slice(0, MAX_FILE_COUNT);
      setFileList(validFileList);
      form.setFieldValue('documents', validFileList);
      return;
    }

    // Update form field
    form.setFieldValue('documents', newFileList);

    // Validate file list
    form.validateFields(['documents']);
  }, [api, form]);

  const handleRemoveFile = useCallback((file: UploadFile) => {
    const newFileList = fileList.filter(item => item.uid !== file.uid);
    setFileList(newFileList);
    form.setFieldValue('documents', newFileList.length > 0 ? newFileList : undefined);
    form.validateFields(['documents']);
  }, [fileList, form]);

  useEffect(() => {
    form.resetFields(['abaRoutingNumber', 'bankStateBranchNumber', 'branchNumber', 'iban']);
  }, [bankData, form]);

  const numberField = useMemo(() => {
    if (accountData && !accountData.abaRoutingNumber && bankData?.country === 'US') {
      return (
        <>
          <Form.Item name='abaRoutingNumber' rules={abaRoutingNumberRules(bankData?.country)}>
            <Input
              placeholder='ABA routing number'
              style={{
                height: '35px',
              }}
              disabled={isFormDisabled}
            />
          </Form.Item>
          <div style={{ marginTop: '-16px', display: 'flex', justifyContent: 'start', marginBottom: '16px' }}>
            <Typography.Text
              style={{
                color: '#F28A28',
                fontSize: 12,
                fontWeight: 400,
                lineHeight: '16px',
              }}>
              {'If not available, please key in 000000.'}
            </Typography.Text>
          </div>
        </>
      );
    }

    if (accountData && !accountData.bankStateBranchNumber && bankData?.country === 'AU') {
      return (
        <>
          <Form.Item name='bankStateBranchNumber' rules={bankStateBranchNumberRules(bankData?.country)}>
            <Input
              placeholder='Bank State Branch number'
              style={{
                height: '35px',
              }}
              disabled={isFormDisabled}
            />
          </Form.Item>
          <div style={{ marginTop: '-16px', display: 'flex', justifyContent: 'start', marginBottom: '16px' }}>
            <Typography.Text
              style={{
                color: '#F28A28',
                fontSize: 12,
                fontWeight: 400,
                lineHeight: '16px',
              }}>
              {'If not available, please key in 000000.'}
            </Typography.Text>
          </div>
        </>
      );
    }

    if (
      accountData &&
      !accountData.branchNumber &&
      bankData &&
      bankData.country !== 'AU' &&
      bankData.country !== 'US' &&
      !isBankAccountFieldOptional(bankData?.country, accountData.currencyCode) &&
      !isIbanRequiredByCountryAndCurrency(bankData?.country, accountData.currencyCode)
    ) {
      return (
        <>
          <Form.Item name='branchNumber' rules={branchNumberRules(bankData?.country, accountData.currencyCode)}>
            <Input
              placeholder='Branch number'
              style={{
                height: '35px',
              }}
              disabled={isFormDisabled}
            />
          </Form.Item>
          <div style={{ marginTop: '-16px', display: 'flex', justifyContent: 'start', marginBottom: '16px' }}>
            <Typography.Text
              style={{
                color: '#F28A28',
                fontSize: 12,
                fontWeight: 400,
                lineHeight: '16px',
              }}>
              {'If not available, please key in 000000.'}
            </Typography.Text>
          </div>
        </>
      );
    }

    return null;
  }, [bankData, accountData, isFormDisabled]);

  const ibanField = useMemo(() => {
    if (
      accountData &&
      !accountData.iban &&
      isIbanRequiredByCountryAndCurrency(bankData?.country, accountData.currencyCode)
    ) {
      return (
        <Form.Item name='iban' rules={ibanRules(bankData?.country, accountData.currencyCode)}>
          <Input
            placeholder='IBAN'
            style={{
              height: '35px',
            }}
            disabled={isFormDisabled}
          />
        </Form.Item>
      );
    }
    return null;
  }, [bankData, accountData, isFormDisabled]);

  const handleBeneficiaryGet = useMemo(() => {
    if (!hasInputBeneficiaryRef.current && !hasInputPaymentRef.current) {
      return '-';
    }
    if (feesPayer === 'SENDER') {
      if (
        roundTo(
          paymentAmount -
          (configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate) /
          exchangeRate,
          paymentDecimal
        ) < 0
      )
        return `-`
      return `${formatNumber(
        beneficiaryAmount.toFixed(beneficiaryDecimal),
        beneficiaryDecimal
      )} ${sendCurrencySymbol}`
    } else {
      if (transferFee === null || serviceFee === null) return `-`
      if (
        roundTo(
          beneficiaryAmount -
          transferFee -
          serviceFee -
          configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate,
          beneficiaryDecimal
        ) < 0
      )
        return `-`
      else
        return `${formatNumber(
          (beneficiaryAmount - transferFee - serviceFee).toFixed(
            beneficiaryDecimal
          ),
          beneficiaryDecimal
        )} ${sendCurrencySymbol}`
    }
  }, [feesPayer, paymentAmount, configFee, exchangeRate, paymentDecimal, beneficiaryAmount, beneficiaryDecimal, sendCurrencySymbol, transferFee, serviceFee, formatNumber, roundTo]);

  const totalToPay = useMemo(() => {
    if (!hasInputBeneficiaryRef.current && !hasInputPaymentRef.current) {
      return '-';
    }
    if (feesPayer === 'SENDER') {
      if (transferFee === null || serviceFee === null) return `-`
      if (
        roundTo(
          paymentAmount -
          (configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate) /
          exchangeRate,
          paymentDecimal
        ) < 0
      )
        return `-`
      return `${formatNumber(
        (paymentAmount + transferFee + serviceFee).toFixed(paymentDecimal),
        paymentDecimal
      )} ${paymentCurrencySymbol}`
    } else {
      // if (transferFee === null || serviceFee === null) return `-`
      if (
        roundTo(
          beneficiaryAmount -
          (transferFee ?? 0) -
          (serviceFee ?? 0) -
          configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate,
          beneficiaryDecimal
        ) < 0
      )
        return `-`
      else
        return `${formatNumber(
          paymentAmount.toFixed(paymentDecimal),
          paymentDecimal
        )} ${paymentCurrencySymbol}`
    }
  }, [feesPayer, transferFee, serviceFee, paymentAmount, configFee, exchangeRate, paymentDecimal, paymentCurrencySymbol, beneficiaryAmount, beneficiaryDecimal, formatNumber, roundTo]);

  const totalFee = useMemo(() => {
    if ((beneficiaryCurrencyData?.type === 'CRYPTO' && !walletData && !bankData) || (!hasInputBeneficiaryRef.current && !hasInputPaymentRef.current)) {
      return '-';
    }
    if (transferFee === null || serviceFee === null) {
      return `-`;
    }
    return `${formatNumber(
      (serviceFee + transferFee).toFixed(
        feesPayer === 'SENDER'
          ? paymentDecimal
          : beneficiaryDecimal
      ),
      feesPayer === 'SENDER'
        ? paymentDecimal
        : beneficiaryDecimal
    )} ${feesPayer === 'SENDER'
      ? paymentCurrencySymbol
      : sendCurrencySymbol
      }`
  }, [beneficiaryCurrencyData, walletData, bankData, transferFee, serviceFee, feesPayer, paymentDecimal, beneficiaryDecimal, paymentCurrencySymbol, sendCurrencySymbol, formatNumber]);

  const transferFeeValue = useMemo(() => {
    if (beneficiaryCurrencyData?.type === 'CRYPTO' && !walletData && !bankData) {
      return '-';
    }
    if (transferFee === null || !customerData) {
      return `-`;
    }
    return `${formatNumber(
      transferFee.toFixed(
        feesPayer === 'SENDER'
          ? paymentDecimal
          : beneficiaryDecimal
      ),
      feesPayer === 'SENDER'
        ? paymentDecimal
        : beneficiaryDecimal
    )} ${feesPayer === 'SENDER'
      ? paymentCurrencySymbol
      : sendCurrencySymbol
      }`;
  }, [beneficiaryCurrencyData, walletData, bankData, transferFee, feesPayer, paymentDecimal, beneficiaryDecimal, paymentCurrencySymbol, sendCurrencySymbol, formatNumber, customerData]);

  const serviceFeeValue = useMemo(() => {
    if (serviceFee === null) {
      return `-`;
    }
    return `${formatNumber(
      serviceFee.toFixed(
        feesPayer === 'SENDER'
          ? paymentDecimal
          : beneficiaryDecimal
      ),
      feesPayer === 'SENDER'
        ? paymentDecimal
        : beneficiaryDecimal
    )} ${feesPayer === 'SENDER'
      ? paymentCurrencySymbol
      : sendCurrencySymbol
      }`;
  }, [serviceFee, feesPayer, paymentDecimal, beneficiaryDecimal, paymentCurrencySymbol, sendCurrencySymbol, formatNumber]);

  const getPaymentDetails = useMemo(() => {
    if (paymentType === PaymentTypes.Fiat) {
      return (
        <>
          {beneficiaryCurrencyData && beneficiaryCurrencyData?.type === 'CRYPTO' ? (
            <>
              <Form.Item
                label="Beneficiary Wallet Address"
                name='beneficiary_wallet_address'
                rules={[
                  // {
                  //   required: true,
                  //   message: 'This field is required',
                  // },
                  ...walletRules(walletData, nextStatus)
                ]}
                style={{ marginTop: '8px' }}
              >
                <AutoComplete<IWallet>
                  resource={`api/v1/company-wallets/companies/${beneficiaryCompanyId}/get-list`}
                  disabled={beneficiaryCompanyId === undefined || isFormDisabled}
                  additionalFilters={[
                    {
                      field: '_currencyId',
                      operator: 'eq' as const,
                      value: sendCurrency,
                    },
                  ]}
                  setLoading={setIsWalletLoading}
                  setData={setWallets}
                  mapItem={mapWalletItem}
                  onSelect={onSelectWallet}
                  allowClear
                  refetchOnEmptyValue
                  onClear={clearWalletData}
                  onClickDropdownButton={() => setShowWalletDetails(true)}
                  enableNotHasData={true}
                  clearOnBlurIfUnselected
                >
                  <Input placeholder='Wallet Address (Name, Currency)' size='large' disabled={isFormDisabled} suffix={<DownOutlined />} />
                </AutoComplete>
              </Form.Item>
              <Details
                show={showWalletDetails}
                setShow={setShowWalletDetails}
                name={'wallet'}
                fields={walletDetailsFields}
                data={walletData}
              />
            </>
          ) : (
            <>
              <Form.Item
                label="Beneficiary Bank Account"
                name='beneficiary_bank_account'
                rules={[
                  // {
                  //   required: true,
                  //   message: 'This field is required',
                  // },
                  ...bankRules(bankData, nextStatus)
                ]}
                style={{ marginTop: '8px' }}
              >
                <AutoComplete<IBankAccount>
                  resource='api/v1/bank_accounts/non_default'
                  disabled={beneficiaryCompanyId === undefined || isFormDisabled}
                  additionalFilters={[
                    {
                      field: 'companyId',
                      operator: 'eq' as const,
                      value: beneficiaryCompanyId,
                    },
                    {
                      field: '_currencyId',
                      operator: 'eq' as const,
                      value: sendCurrency,
                    },
                  ]}
                  setLoading={setIsBankLoading}
                  setData={setBankAccounts}
                  mapItem={mapBankAccountItem}
                  onSelect={onSelectBankAccount}
                  allowClear
                  refetchOnEmptyValue
                  onClear={clearBankData}
                  onClickDropdownButton={() => setShowBankDetails(true)}
                  enableNotHasData={true}
                  clearOnBlurIfUnselected
                >
                  <Input placeholder='Choose a bank account' size='large' disabled={isFormDisabled} suffix={<DownOutlined />} />
                </AutoComplete>
              </Form.Item>
              <Details
                show={showBankDetails}
                setShow={setShowBankDetails}
                name={'bank'}
                fields={bankDetailsFields}
                data={bankData}
              />
            </>
          )}
          {numberField}
          {ibanField}
        </>
      );
    }
    return (
      <>
        {!companyData?.refUser?.smartUID ? (
          <Typography
            style={{
              fontSize: '12px',
              fontWeight: 400,
              color: '#ff4d4f',
              marginBottom: '16px',
            }}>
            Please select or create a beneficiary that has a SMART UID
          </Typography>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              marginTop: '8px',
            }}>
            <Form.Item
              style={{ width: '100%' }}
              label='SMART UID'
              validateTrigger='onBlur'
              name='smartUID'
              rules={[
                { required: true, message: 'This field is required' },
                {
                  max: 256,
                  message: 'Maximum 256 characters',
                  validateTrigger: 'onBlur',
                },
              ]}
              className='input-user-id'>
              <Input
                disabled
                placeholder='Enter the code linked to your SMART account'
              />
            </Form.Item>
            {beneficiary ? null : (
              <Typography
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  color: '#EC3333',
                  marginBottom: 6,
                  marginTop: '-12px',
                }}>
                {
                  'Please select a beneficiary before choosing a SMART UID.'
                }
              </Typography>
            )}
          </div>
        )}
      </>
    );
  }, [
    paymentType,
    bankData,
    isFormDisabled,
    nextStatus,
    onSelectBankAccount,
    clearBankData,
    numberField,
    ibanField,
    showBankDetails,
    setShowBankDetails,
    beneficiaryCompanyId,
    sendCurrency,
    beneficiary,
    walletData,
    onSelectWallet,
    clearWalletData,
    showWalletDetails,
    setShowWalletDetails,
    beneficiaryCurrencyData,
    companyData?.refUser?.smartUID,
  ]);

  const getCurrencyDetails = useMemo(() => {
    return (
      <>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 16 }}>
          <Form.Item
            label="Beneficiary gets"
            name='beneficiary_value'
            rules={[
              { required: true, message: 'This field is required' },
              {
                validator: async (_, value) => {
                  if (!hasInputBeneficiaryRef.current) {
                    return Promise.resolve()
                  }
                  if (!value) {
                    return Promise.resolve();
                  }
                  if (Number(value) <= 0) {
                    return Promise.reject(
                      new Error('The amount must be greater than 0')
                    );
                  }

                  // Max limit check theo currency
                  const maxLimit = getMaxLimit(sendCurrency || beneficiaryCurrencyId);

                  if (maxLimit !== null && Number(value) > maxLimit) {
                    return Promise.reject(
                      new Error(
                        `Amount exceeds the maximum allowed limit of ${formatNumber(
                          maxLimit,
                          beneficiaryDecimal
                        )}`
                      )
                    );
                  }

                  // case: sender pay fee
                  if (feesPayer === 'SENDER') {
                    // Skip min amount validation if configFee is not available or loading
                    // This prevents validation with old fee data when currency is changed
                    if (!configFee || loadingFee) {
                      // Use preserved error if:
                      // 1. We're actively changing currency, OR
                      // 2. configFee is null/loading and we have a preserved error (currency was just changed)
                      // This ensures errors are preserved during the entire currency change process
                      if (preservedBeneficiaryErrorRef.current && (isChangingCurrencyRef.current || !configFee)) {
                        return Promise.reject(new Error(preservedBeneficiaryErrorRef.current));
                      }
                      // If there's an existing error, keep it (don't clear)
                      // This prevents clearing errors when configFee is temporarily null during currency change
                      const currentErrors = form.getFieldError('beneficiary_value');
                      if (currentErrors.length > 0) {
                        // Return the existing error to preserve it
                        return Promise.reject(new Error(currentErrors[0]));
                      }
                      // If not changing currency and configFee is not available, trigger a fetch
                      // This ensures configFee is available for validation
                      if (!isChangingCurrencyRef.current && !configFee && (sendCurrency || beneficiaryCurrencyId)) {
                        const currencyForFee = sendCurrency || beneficiaryCurrencyId;
                        if (currencyForFee) {
                          fetchConfigurationFee(currencyForFee);
                        }
                      }
                      // Return resolve to allow validation to retry when configFee is available
                      // The re-validation useEffect will trigger validation again when configFee is fetched
                      return Promise.resolve();
                    }

                    const minBeneficiary = roundTo(
                      configFee?.bufferUsd *
                      configFee?.usdToFeeCurrencyRate,
                      beneficiaryDecimal
                    );

                    if (Number(value) < minBeneficiary) {
                      setBeneficiaryValueError(true);
                      const errorMsg = `Your beneficiary get must be at least ${formatNumber(minBeneficiary, beneficiaryDecimal)}`;
                      return Promise.reject(new Error(errorMsg));
                    }
                  }

                  // case: beneficiary pay fee
                  if (feesPayer === 'BENEFICIARY') {
                    // Skip min amount validation if configFee is not available or loading
                    // This prevents validation with old fee data when currency is changed
                    if (!configFee || loadingFee) {
                      // Use preserved error if:
                      // 1. We're actively changing currency, OR
                      // 2. configFee is null/loading and we have a preserved error (currency was just changed)
                      // This ensures errors are preserved during the entire currency change process
                      if (preservedBeneficiaryErrorRef.current && (isChangingCurrencyRef.current || !configFee)) {
                        return Promise.reject(new Error(preservedBeneficiaryErrorRef.current));
                      }
                      // If there's an existing error, keep it (don't clear)
                      const currentErrors = form.getFieldError('beneficiary_value');
                      if (currentErrors.length > 0) {
                        return Promise.reject(new Error(currentErrors[0]));
                      }
                      return Promise.resolve();
                    }

                    // If transferFee or serviceFee is null, skip validation to avoid false positives
                    // The validation will be triggered again when fees are calculated
                    if (transferFee === null || serviceFee === null) {
                      // Use preserved error if:
                      // 1. We're actively changing currency, OR
                      // 2. Fees are null and we have a preserved error (currency was just changed)
                      if (preservedBeneficiaryErrorRef.current && (isChangingCurrencyRef.current || (transferFee === null || serviceFee === null))) {
                        return Promise.reject(new Error(preservedBeneficiaryErrorRef.current));
                      }
                      // If there's an existing error, keep it (don't clear)
                      const currentErrors = form.getFieldError('beneficiary_value');
                      if (currentErrors.length > 0) {
                        return Promise.reject(new Error(currentErrors[0]));
                      }
                      // Don't validate yet, wait for fees to be calculated
                      // Return resolve to avoid blocking, but validation will be retried when fees are ready
                      return Promise.resolve();
                    }

                    const minBeneficiary = roundTo(
                      transferFee +
                      serviceFee +
                      configFee?.bufferUsd *
                      configFee?.usdToFeeCurrencyRate,
                      beneficiaryDecimal
                    );

                    if (Number(value) < minBeneficiary) {
                      setBeneficiaryValueError(true);
                      const errorMsg = `Your beneficiary get must be at least ${formatNumber(minBeneficiary, beneficiaryDecimal)}`;
                      return Promise.reject(new Error(errorMsg));
                    }
                  }

                  setBeneficiaryValueError(false);
                  // Clear preserved error since validation passed
                  preservedBeneficiaryErrorRef.current = null;
                  return Promise.resolve();
                },
              },
            ]}
            style={{ width: '48%' }}
            validateTrigger='onBlur'
          >
            <Input
              placeholder='Beneficiary amount'
              size='middle'
              style={inputFormStyles}
              onChange={handleChangeBeneficiaryValue}
              onBlur={handleBlurSend}
              onKeyDown={(e) => {
                const allowedKeys = [
                  'Backspace',
                  'Delete',
                  'ArrowLeft',
                  'ArrowRight',
                  'Tab',
                ];
                if (allowedKeys.includes(e.key)) return;

                // Only allow numbers or decimal point
                if (!/^[0-9.]$/.test(e.key)) {
                  e.preventDefault();
                  return;
                }

                const { value, selectionStart } = e.currentTarget;

                // Only allow one decimal point
                if (e.key === '.' && value?.includes('.')) {
                  e.preventDefault();
                  return;
                }

                // Check number of decimal places
                if (value?.includes('.')) {
                  const dotIndex = value?.indexOf('.');
                  const decimals = value?.substring(dotIndex + 1);

                  // If typing after decimal point, check length
                  if (
                    selectionStart !== null &&
                    selectionStart > dotIndex &&
                    decimals.length >= beneficiaryDecimal
                  ) {
                    e.preventDefault();
                  }
                }
              }}
              disabled={isFormDisabled}
            />
          </Form.Item>
          <Form.Item
            label="Beneficiary currency"
            name='beneficiary_currency'
            rules={currencyRules(currencyData, nextStatus)}
            style={{
              width: '48%',
            }}
          >
            <AutoComplete
              style={{
                marginTop: '0px',
              }}
              resource='api/v1/asset-config-groups/get-all'
              mapItem={mapCurrencyItemWithIcon}
              enableLoadMore={true}
              pageSize={100}
              enableNotHasData={true}
              prioritizeFiatType={true}
              readOnly={true}
              onSelect={async (_, { data }) => {
                setIsChangingCurrency(true);
                isChangingCurrencyRef.current = true;

                // Preserve beneficiary_value error before clearing configFee
                const beneficiaryErrors = form.getFieldError('beneficiary_value');
                const hasMinAmountError = beneficiaryErrors.some((err: string) => err?.includes('must be at least'));
                if (hasMinAmountError) {
                  preservedBeneficiaryErrorRef.current = beneficiaryErrors[0] || null;
                  setPreservedBeneficiaryError(beneficiaryErrors[0] || null);
                } else {
                  preservedBeneficiaryErrorRef.current = null;
                  setPreservedBeneficiaryError(null);
                }

                setConfigFee(null);
                setTransferFee(null);
                setServiceFee(null);

                // Reset related fields when beneficiary currency changes (as per doc requirement)
                setCompanyData(undefined);
                setBankData(undefined);
                setAccountData(undefined);
                setServiceProviderData(undefined);
                setWalletData(undefined);
                form.setFieldValue('bankId', undefined);
                form.setFieldValue('bankAccountId', undefined);
                form.setFieldValue('beneficiary_wallet_address', undefined);
                form.setFieldValue('beneficiary_bank_account', undefined);
                form.setFieldValue('beneficiary', undefined);

                const currencyId = (data as any).id;
                if (!currencyId) return;

                const currency = allCurrencies.find((item: any) => item.id === currencyId);
                if (currency) {
                  setCurrencyData(currency);
                  setBeneficiaryCurrencyId(currencyId);
                  setSendCurrency(currencyId);
                  const symbol = currency.symbol || currency.code;
                  setSendCurrencySymbol(symbol);
                  setBeneficiaryDecimal(currency.decimals || 2);
                  // Set form field value to symbol for display
                  form.setFieldValue('beneficiary_currency', symbol);
                  setBeneficiaryCurrencyData(currency);
                }

                const currentBeneficiaryValue = form.getFieldValue('beneficiary_value');
                const currentPaymentValue = form.getFieldValue('payment_value');
                const beneficiaryValueNum = Number(currentBeneficiaryValue) || 0;
                const paymentValueNum = Number(currentPaymentValue) || 0;

                // Determine which field to keep based on last change
                const keepBeneficiary = lastChange === 'BENEFICIARY' && beneficiaryValueNum > 0;
                const keepPayment = lastChange === 'SENDER' && paymentValueNum > 0 && !keepBeneficiary;

                // Fetch new exchange rate - always call API when currency changes
                let newRate = exchangeRate;
                if (paymentCurrencyId && currencyId !== paymentCurrencyId) {
                  try {
                    const rateResult = await getExchangeRate({ from: paymentCurrencyId, to: currencyId });
                    newRate = rateResult?.data?.data?.data || rateResult?.data?.data || rateResult?.data || exchangeRate;
                    if (newRate && newRate > 0) {
                      setExchangeRate(newRate);
                    } else {
                      // If rate is invalid, use 1 as fallback
                      newRate = 1;
                      setExchangeRate(1);
                    }
                  } catch (error) {
                    // Use existing rate if fetch fails, or 1 as fallback
                    newRate = exchangeRate || 1;
                  }
                } else if (currencyId === paymentCurrencyId) {
                  newRate = 1;
                  setExchangeRate(1);
                }

                if (keepBeneficiary && beneficiaryValueNum > 0) {
                  // Keep beneficiary value, recalculate payment
                  const paymentCurrency = allCurrencies.find((item: any) => item.id === paymentCurrencyId);
                  if (paymentCurrency) {
                    const calculatedPayment = (beneficiaryValueNum / newRate).toFixed(paymentCurrency.decimals || 2);
                    const calculatedPaymentNum = Number(calculatedPayment);

                    form.setFieldsValue({
                      beneficiary_value: beneficiaryValueNum.toFixed(currency.decimals || 2),
                      payment_value: calculatedPayment,
                    });
                    setPaymentAmount(calculatedPaymentNum);
                    setBeneficiaryAmount(beneficiaryValueNum);
                    setLastChange('BENEFICIARY');
                    setLastChangedField('beneficiary');
                    setDirection('BEN_TO_PAY');
                  }
                } else if (keepPayment && paymentValueNum > 0) {
                  // Keep payment value, recalculate beneficiary
                  const calculatedBeneficiary = (paymentValueNum * newRate).toFixed(currency.decimals || 2);
                  const calculatedBeneficiaryNum = Number(calculatedBeneficiary);

                  form.setFieldsValue({
                    beneficiary_value: calculatedBeneficiary,
                    payment_value: paymentValueNum.toFixed(paymentDecimal),
                  });
                  setPaymentAmount(paymentValueNum);
                  setBeneficiaryAmount(calculatedBeneficiaryNum);
                  setLastChange('SENDER');
                  setLastChangedField('payment');
                  setDirection('PAY_TO_BEN');
                } else {
                  // Set default value if no existing values - use 1000 as default
                  // const defaultValue = '1000.00';
                  const defaultValue = '0.00';
                  const defaultValueNum = Number(defaultValue);
                  form.setFieldValue('beneficiary_value', defaultValue);
                  setBeneficiaryAmount(defaultValueNum);
                  if (paymentCurrencyId) {
                    const paymentCurrency = allCurrencies.find((item: any) => item.id === paymentCurrencyId);
                    if (paymentCurrency) {
                      // Calculate payment value from beneficiary value and rate
                      const calculatedPayment = (defaultValueNum / newRate).toFixed(paymentCurrency.decimals || 2);
                      const calculatedPaymentNum = Number(calculatedPayment);
                      form.setFieldValue('payment_value', calculatedPayment);
                      setPaymentAmount(calculatedPaymentNum);
                    }
                  }
                  setLastChange('BENEFICIARY');
                  setLastChangedField('beneficiary');
                  setDirection('BEN_TO_PAY');
                }

                // Always recalculate and validate after currency change
                setTimeout(() => {
                  setIsChangingCurrency(false);
                  isChangingCurrencyRef.current = false;
                  // Re-validate to ensure calculations are correct
                  const finalBeneficiaryValue = form.getFieldValue('beneficiary_value');
                  const finalPaymentValue = form.getFieldValue('payment_value');

                  if (finalBeneficiaryValue && finalPaymentValue && paymentCurrencyId) {
                    const paymentCurrency = allCurrencies.find((item: any) => item.id === paymentCurrencyId);
                    if (paymentCurrency) {
                      // Double-check calculation
                      const beneficiaryNum = Number(finalBeneficiaryValue);
                      const paymentNum = Number(finalPaymentValue);
                      const expectedPayment = (beneficiaryNum / newRate).toFixed(paymentCurrency.decimals || 2);

                      if (Math.abs(paymentNum - Number(expectedPayment)) > 0.01) {
                        form.setFieldValue('payment_value', expectedPayment);
                        setPaymentAmount(Number(expectedPayment));
                      }
                    }
                  }

                  // Re-validate after fees are recalculated
                  const validateAfterFees = () => {
                    if (feesPayer === 'BENEFICIARY' && (transferFee === null || serviceFee === null)) {
                      // Wait for fees to be calculated
                      setTimeout(validateAfterFees, 100);
                      return;
                    }

                    // Always validate both fields when currency changes
                    form.validateFields(['beneficiary_value', 'payment_value']).then(() => {
                      // Clear preserved error if validation passes
                      preservedBeneficiaryErrorRef.current = null;
                      setPreservedBeneficiaryError(null);
                    }).catch(() => {
                      // Validation errors are expected - trigger validation again to show errors
                      setTimeout(() => {
                        form.validateFields(['beneficiary_value', 'payment_value']).catch(() => {
                          // Validation errors are expected - ignore
                        });
                      }, 100);
                    });
                  };

                  setTimeout(validateAfterFees, 200);
                  // Also validate immediately after a short delay
                  setTimeout(() => {
                    form.validateFields(['beneficiary_value', 'payment_value']).catch(() => {
                      // Validation errors are expected - ignore
                    });
                  }, 300);
                }, 150);
              }}
              onClear={() => {
                clearCurrencyData();
                setBeneficiaryCurrencyId(undefined);
                setSendCurrency(undefined);
              }}
              refetchOnEmptyValue
              disabled={isFormDisabled}
            >
              <Input
                placeholder='Beneficiary currency'
                size='large'
                disabled={isFormDisabled}
                readOnly={true}
                onKeyDown={(e) => e.preventDefault()}
                suffix={<DownOutlined />}
              />
            </AutoComplete>
          </Form.Item>
        </div>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 16, marginTop: 16 }}>
          <Form.Item
            label="Payment amount"
            name='payment_value'
            rules={[
              { required: true, message: 'This field is required' },
              {
                validator: async (_, value) => {
                  if (!hasInputPaymentRef.current) {
                    return Promise.resolve()
                  }
                  if (!value) return Promise.resolve();

                  // Allow incomplete values (like "." or "0.") during typing
                  // Only validate complete numbers
                  if (value === '.' || (value.endsWith('.') && !value.startsWith('.'))) {
                    return Promise.resolve();
                  }

                  // Check if value is a valid number
                  const numValue = Number(value);
                  if (isNaN(numValue) || numValue <= 0) {
                    return Promise.reject(
                      new Error('The amount must be greater than 0')
                    );
                  }

                  // Max limit check theo currency - use form field value to ensure we use the latest currency
                  const currentPaymentCurrency = form.getFieldValue('payment_currency') || paymentCurrency || paymentCurrencyId;
                  const maxLimit = getMaxLimit(currentPaymentCurrency);

                  if (maxLimit !== null && numValue > maxLimit) {
                    return Promise.reject(
                      new Error(
                        `Amount exceeds the maximum allowed limit of ${formatNumber(
                          maxLimit,
                          paymentDecimal
                        )}`
                      )
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
            style={{ width: '48%' }}
            validateTrigger='onBlur'
          >
            <Input
              placeholder='Payment amount'
              size='middle'
              style={inputFormStyles}
              onChange={handleChangePaymentValue}
              onBlur={handleBlurPayment}
              onKeyDown={(e) => {
                const allowedKeys = [
                  'Backspace',
                  'Delete',
                  'ArrowLeft',
                  'ArrowRight',
                  'Tab',
                ];
                if (allowedKeys?.includes(e.key)) return;

                // Only allow numbers or decimal point
                if (!/^[0-9.]$/.test(e.key)) {
                  e.preventDefault();
                  return;
                }

                const { value, selectionStart } = e.currentTarget;

                // Only allow one decimal point
                if (e.key === '.' && value?.includes('.')) {
                  e.preventDefault();
                  return;
                }

                // Check number of decimal places
                if (value?.includes('.')) {
                  const dotIndex = value?.indexOf('.');
                  const decimals = value?.substring(dotIndex + 1);

                  // If typing after decimal point, check length
                  if (
                    selectionStart !== null &&
                    selectionStart > dotIndex &&
                    decimals.length >= paymentDecimal
                  ) {
                    e.preventDefault();
                  }
                }
              }}
              disabled={isFormDisabled}
            />
          </Form.Item>
          <Form.Item
            label="Payment currency"
            name='payment_currency'
            rules={currencyRules(paymentCurrencyData, nextStatus)}
            style={{
              width: '48%',
            }}
          >
            <AutoComplete
              enableNotHasData={true}
              style={{
                marginTop: '0px',
              }}
              pageSize={100}
              resource='api/v1/asset-config-groups/get-all'
              mapItem={mapCurrencyUserItemWithIcon}
              prioritizeFiatType={true}
              readOnly={true}
              onSelect={async (_, { data }) => {
                setIsChangingCurrency(true);
                isChangingCurrencyRef.current = true;

                // Preserve beneficiary_value error before clearing configFee
                const beneficiaryErrors = form.getFieldError('beneficiary_value');
                const hasMinAmountError = beneficiaryErrors.some((err: string) => err?.includes('must be at least'));
                if (hasMinAmountError) {
                  preservedBeneficiaryErrorRef.current = beneficiaryErrors[0] || null;
                  setPreservedBeneficiaryError(beneficiaryErrors[0] || null);
                } else {
                  preservedBeneficiaryErrorRef.current = null;
                  setPreservedBeneficiaryError(null);
                }

                setConfigFee(null);
                setTransferFee(null);
                setServiceFee(null);

                // Reset related fields when currency changes (as per doc requirement)
                // Note: Beneficiary, bank account, smartUID, provider should be reset
                // But we only reset if payment currency changes affect beneficiary currency requirements
                // For now, we keep beneficiary selection as it might be currency-agnostic

                // Clear provider and related fields when payment currency changes
                setServiceProviderData(undefined);
                setProviderBankAccountData(undefined);
                setProviderWalletData(undefined);
                form.setFieldValue('serviceProviderId', undefined);
                form.setFieldValue('providerBankAccount', undefined);
                form.setFieldValue('providerWallet', undefined);

                const currencyId = (data as any).id;
                if (!currencyId) return;

                const currency = allCurrencies.find((item: any) => item.id === currencyId);
                if (currency) {
                  setPaymentCurrencyData(currency);
                  setPaymentCurrencyId(currencyId);
                  setPaymentCurrency(currencyId);
                  setPaymentCurrencySymbol(currency.symbol || currency.code);
                  setPaymentDecimal(currency.decimals || 2);
                }

                const currentBeneficiaryValue = form.getFieldValue('beneficiary_value');
                const currentPaymentValue = form.getFieldValue('payment_value');
                const beneficiaryValueNum = Number(currentBeneficiaryValue) || 0;
                const paymentValueNum = Number(currentPaymentValue) || 0;

                // Determine which field to keep based on last change
                const keepBeneficiary = lastChange === 'BENEFICIARY' && beneficiaryValueNum > 0;
                const keepPayment = lastChange === 'SENDER' && paymentValueNum > 0 && !keepBeneficiary;

                // Fetch new exchange rate - always call API when currency changes
                let newRate = exchangeRate;
                if (beneficiaryCurrencyId && currencyId !== beneficiaryCurrencyId) {
                  try {
                    const rateResult = await getExchangeRate({ from: currencyId, to: beneficiaryCurrencyId });
                    newRate = rateResult?.data?.data?.data || rateResult?.data?.data || rateResult?.data || exchangeRate;
                    if (newRate && newRate > 0) {
                      setExchangeRate(newRate);
                    } else {
                      // If rate is invalid, use 1 as fallback
                      newRate = 1;
                      setExchangeRate(1);
                    }
                  } catch (error) {
                    console.error('Error fetching exchange rate:', error);
                    // Use existing rate if fetch fails, or 1 as fallback
                    newRate = exchangeRate || 1;
                  }
                } else if (currencyId === beneficiaryCurrencyId) {
                  newRate = 1;
                  setExchangeRate(1);
                }

                if (keepBeneficiary && beneficiaryValueNum > 0) {
                  // Keep beneficiary value, recalculate payment
                  const calculatedPayment = (beneficiaryValueNum / newRate).toFixed(currency.decimals || 2);
                  const calculatedPaymentNum = Number(calculatedPayment);

                  form.setFieldsValue({
                    beneficiary_value: beneficiaryValueNum.toFixed(beneficiaryDecimal),
                    payment_value: calculatedPayment,
                  });
                  setPaymentAmount(calculatedPaymentNum);
                  setBeneficiaryAmount(beneficiaryValueNum);
                  setLastChange('BENEFICIARY');
                  setLastChangedField('beneficiary');
                  setDirection('BEN_TO_PAY');
                } else if (keepPayment && paymentValueNum > 0) {
                  // Keep payment value, recalculate beneficiary
                  const beneficiaryCurrency = allCurrencies.find((item: any) => item.id === beneficiaryCurrencyId);
                  if (beneficiaryCurrency) {
                    const calculatedBeneficiary = (paymentValueNum * newRate).toFixed(beneficiaryCurrency.decimals || 2);
                    const calculatedBeneficiaryNum = Number(calculatedBeneficiary);

                    form.setFieldsValue({
                      beneficiary_value: calculatedBeneficiary,
                      payment_value: paymentValueNum.toFixed(currency.decimals || 2),
                    });
                    setPaymentAmount(paymentValueNum);
                    setBeneficiaryAmount(calculatedBeneficiaryNum);
                    setLastChange('SENDER');
                    setLastChangedField('payment');
                    setDirection('PAY_TO_BEN');
                  }
                } else {
                  // Set default value if no existing values - use 1000 as default
                  if (beneficiaryCurrencyId) {
                    // const defaultValue = '1000.00';
                    const defaultValue = '0.00';
                    const defaultValueNum = Number(defaultValue);

                    form.setFieldValue('beneficiary_value', defaultValue);
                    setBeneficiaryAmount(defaultValueNum);
                    // Calculate payment value from beneficiary value and rate
                    const calculatedPayment = (defaultValueNum / newRate).toFixed(currency.decimals || 2);
                    const calculatedPaymentNum = Number(calculatedPayment);
                    form.setFieldValue('payment_value', calculatedPayment);
                    setPaymentAmount(calculatedPaymentNum);
                  }
                  setLastChange('BENEFICIARY');
                  setLastChangedField('beneficiary');
                  setDirection('BEN_TO_PAY');
                }

                // Always recalculate and validate after currency change
                setTimeout(() => {
                  setIsChangingCurrency(false);
                  isChangingCurrencyRef.current = false;
                  // Re-validate to ensure calculations are correct
                  const finalBeneficiaryValue = form.getFieldValue('beneficiary_value');
                  const finalPaymentValue = form.getFieldValue('payment_value');

                  if (finalBeneficiaryValue && finalPaymentValue && beneficiaryCurrencyId) {
                    const beneficiaryCurrency = allCurrencies.find((item: any) => item.id === beneficiaryCurrencyId);
                    if (beneficiaryCurrency) {
                      // Double-check calculation
                      const paymentNum = Number(finalPaymentValue);
                      const beneficiaryNum = Number(finalBeneficiaryValue);
                      const expectedBeneficiary = (paymentNum * newRate).toFixed(beneficiaryCurrency.decimals || 2);

                      if (Math.abs(beneficiaryNum - Number(expectedBeneficiary)) > 0.01) {
                        form.setFieldValue('beneficiary_value', expectedBeneficiary);
                        setBeneficiaryAmount(Number(expectedBeneficiary));
                      }
                    }
                  }

                  // Re-validate after fees are recalculated
                  const validateAfterFees = () => {
                    if (feesPayer === 'BENEFICIARY' && (transferFee === null || serviceFee === null)) {
                      // Wait for fees to be calculated
                      setTimeout(validateAfterFees, 100);
                      return;
                    }

                    // Always validate both fields when currency changes
                    form.validateFields(['beneficiary_value', 'payment_value']).then(() => {
                      // Clear preserved error if validation passes
                      preservedBeneficiaryErrorRef.current = null;
                      setPreservedBeneficiaryError(null);
                    }).catch(() => {
                      // Validation errors are expected - trigger validation again to show errors
                      setTimeout(() => {
                        form.validateFields(['beneficiary_value', 'payment_value']).catch(() => {
                          // Validation errors are expected - ignore
                        });
                      }, 100);
                    });
                  };

                  setTimeout(validateAfterFees, 200);
                  // Also validate immediately after a short delay
                  setTimeout(() => {
                    form.validateFields(['beneficiary_value', 'payment_value']).catch(() => {
                      // Validation errors are expected - ignore
                    });
                  }, 300);
                }, 150);
              }}
              onClear={() => {
                clearPaymentCurrencyData();
                setPaymentCurrencyId(undefined);
                setPaymentCurrency(undefined);
              }}
              refetchOnEmptyValue
              disabled={isFormDisabled}
            >
              <Input placeholder='Payment currency' size='large' disabled={isFormDisabled} suffix={<DownOutlined />} />
            </AutoComplete>
          </Form.Item>
        </div>
        {beneficiaryCurrencyId && paymentCurrencyId && exchangeRate && (() => {
          const rateValue = 1 / exchangeRate;
          const initialDecimals = paymentCurrencyData?.decimals || 2;
          const { value: roundedValue, decimals: finalDecimals } = roundDownWithMinimum(rateValue, initialDecimals);
          return (
            <Typography
              style={{
                fontSize: '12px',
                fontWeight: 400,
                color: 'rgba(242, 138, 40, 1)',
              }}
            >
              Indicated rate (1 minute): 1 {sendCurrencySymbol || currencyData?.symbol || currencyData?.code || ''} ≈{' '}
              {formatNumber(roundedValue, finalDecimals)}{' '}
              {paymentCurrencySymbol || paymentCurrencyData?.symbol || paymentCurrencyData?.code || ''}
            </Typography>
          );
        })()}
      </>
    );
  }, [
    allCurrencies,
    form,
    exchangeRate,
    beneficiaryCurrencyId,
    paymentCurrencyId,
    sendCurrency,
    paymentCurrency,
    currencyData,
    paymentCurrencyData,
    beneficiaryDecimal,
    paymentDecimal,
    feesPayer,
    configFee,
    loadingFee,
    nextStatus,
    getMaxLimit,
    formatNumber,
    roundTo,
    roundDownWithMinimum,
    handleChangeBeneficiaryValue,
    handleChangePaymentValue,
    handleBlurSend,
    handleBlurPayment,
    preservedBeneficiaryErrorRef,
    isChangingCurrencyRef,
    sendCurrencySymbol,
    paymentCurrencySymbol,
    isFormDisabled,
    fetchConfigurationFee,
    setBeneficiaryValueError,
    transferFee,
    serviceFee,
    lastChange,
    setIsChangingCurrency,
    setConfigFee,
    setTransferFee,
    setServiceFee,
    setCompanyData,
    setBankData,
    setAccountData,
    setServiceProviderData,
    setCurrencyData,
    setBeneficiaryCurrencyId,
    setSendCurrency,
    setSendCurrencySymbol,
    setBeneficiaryDecimal,
    setPaymentCurrencyData,
    setPaymentCurrencyId,
    setPaymentCurrency,
    setPaymentCurrencySymbol,
    setPaymentDecimal,
    setPaymentAmount,
    setBeneficiaryAmount,
    setLastChange,
    setLastChangedField,
    setDirection,
    clearCurrencyData,
    clearPaymentCurrencyData,
    mapCurrencyItemWithIcon,
    mapCurrencyUserItemWithIcon,
  ]);

  return (
    <>
      {differentCurrenciesContextHolder}
      {unsavedChangesContextHolder}
      <div style={{ padding: '16px', maxWidth: '100%', backgroundColor: '#fff' }}>
        <Typography.Title level={3} style={{ marginBottom: 24 }}>
          Create Send Money
        </Typography.Title>
        <Form
          form={form}
          layout='vertical'
          onFinish={onSubmit}
          disabled={isCreating}
          validateTrigger={false}
          onValuesChange={(changedValues, allValues) => {
            const changedFieldName = Object.keys(changedValues)[0];

            // Don't clear validation errors when changing currency
            if (isChangingCurrency) {
              return;
            }

            // IMPORTANT: Never clear errors when changing currency fields
            // Currency changes will trigger their own validation logic
            if (changedFieldName === 'payment_currency' || changedFieldName === 'beneficiary_currency') {
              // If payment_currency changed and we have a preserved error, ensure it's set
              if (changedFieldName === 'payment_currency' && preservedBeneficiaryError) {
                const currentErrors = form.getFieldError('beneficiary_value');
                if (currentErrors.length === 0) {
                  // Set the preserved error to ensure it's displayed
                  form.setFields([{
                    name: 'beneficiary_value',
                    errors: [preservedBeneficiaryError]
                  }]);
                }
              }
              handleFormValuesChange(changedValues, allValues);
              return;
            }

            // Check if beneficiary_value or payment_value has errors
            // NEVER clear errors if field still has validation errors
            const beneficiaryErrors = form.getFieldError('beneficiary_value');
            const paymentErrors = form.getFieldError('payment_value');
            const hasBeneficiaryError = beneficiaryErrors.length > 0;
            const hasPaymentError = paymentErrors.length > 0;

            // If beneficiary_value or payment_value is being changed and has errors, don't clear
            if (changedFieldName === 'beneficiary_value' || changedFieldName === 'payment_value') {
              if (hasBeneficiaryError || hasPaymentError) {
                handleFormValuesChange(changedValues, allValues);
                return;
              }
            }

            // If changing other fields, check if beneficiary_value or payment_value has errors
            // If they have errors, don't clear them (only clear errors for the changed field if it's not beneficiary_value or payment_value)
            if (hasBeneficiaryError || hasPaymentError) {
              // Only clear errors for fields that are not beneficiary_value or payment_value
              const fieldsToClear: string[] = [];
              Object.keys(changedValues).forEach(fieldName => {
                if (fieldName !== 'beneficiary_value' && fieldName !== 'payment_value') {
                  fieldsToClear.push(fieldName);
                }
              });
              if (fieldsToClear.length > 0) {
                // Only clear errors for non-beneficiary/payment fields
                const filteredChangedValues: any = {};
                fieldsToClear.forEach(fieldName => {
                  filteredChangedValues[fieldName] = changedValues[fieldName];
                });
                clearFieldErrorMessageOnChange(form)(filteredChangedValues);
              }
              handleFormValuesChange(changedValues, allValues);
              return;
            }

            clearFieldErrorMessageOnChange(form)(changedValues);
            handleFormValuesChange(changedValues, allValues);
          }}
        >
          {/* Customer Selection - REQUIRED FIRST - Always at the top */}
          {/* All other fields are disabled until customer is selected */}
          <Form.Item
            label="Customer"
            name='customerId'
            rules={[
              // ...customerRules(customerData),
              {
                required: true,
                message: 'Please select a customer first',
              },
            ]}
            style={{ marginBottom: 24 }}
            required
          >
            <AutocompleteCustomerInput
              onSelect={onSelectCustomer}
              refetchOnEmptyValue
              onClear={clearCustomerData}
              allowClear
              enableLoadMore={true}
            />
          </Form.Item>
          <Details
            show={showCustomerDetails}
            setShow={setShowCustomerDetails}
            name={'customer'}
            fields={customerDetailsFieldsSmartUID}
            data={customerData}
          />

          {/* Rest of the form - disabled until customer is selected */}
          {!customerData && (
            <Alert
              message="Please select a customer first"
              description="You must select a customer before filling in the payment request details."
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}
          {getCurrencyDetails}

          <Form.Item
            label="Beneficiary"
            name='beneficiary'
            rules={[
              // {
              //   required: true,
              //   message: 'This field is required',
              // },
              ...companyRules(companyData, nextStatus)
            ]}
          >
            <AutoComplete
              enableNotHasData={true}
              resource='api/v1/companies'
              additionalFilters={[
                companySearchFieldNameFilter,
                {
                  field: '_isDefault',
                  operator: 'eq' as const,
                  value: false,
                },
                {
                  field: '_isProvider',
                  operator: 'eq' as const,
                  value: false,
                },
                ...(customerData?.id ? [{
                  field: '_userId',
                  operator: 'eq' as const,
                  value: customerData.id,
                }] : []),
              ]}
              mapItem={mapCompanyUserItem}
              onSelect={onSelectCompany}
              allowClear
              enableLoadMore={true}
              pageSize={20}
              refetchOnEmptyValue
              onClear={clearCompanyData}
              clearOnBlurIfUnselected
              disabled={isFormDisabled}
            >
              <Input placeholder='Beneficiary' size='large' disabled={isFormDisabled} suffix={<DownOutlined />} />
            </AutoComplete>
          </Form.Item>
          <Details
            show={showCompanyDetails}
            setShow={setShowCompanyDetails}
            name={'company'}
            fields={companyBeneficiaryDetailsFields}
            data={companyData}
          />

          {beneficiary && (
            <>
              <Radio.Group
                value={paymentType}
                onChange={e => {
                  setPaymentType(e.target.value);
                }}
                disabled={isFormDisabled || !beneficiaryCompanyId}
                style={{ marginTop: 16, marginBottom: 16 }}
              >
                <Radio.Button style={{ borderRadius: 0 }} value={PaymentTypes.Fiat}>
                  {beneficiaryCurrencyData?.type === 'CRYPTO' ? 'Crypto Network' : 'Bank Transfer'}
                </Radio.Button>
                {companyData?.refUser?.smartUID ? (
                  <Radio.Button style={{ borderRadius: 0 }} value={PaymentTypes.EWallet}>
                    SMART Wallet
                  </Radio.Button>
                ) : (
                  <></>
                )}
              </Radio.Group>
              {getPaymentDetails}
            </>
          )}

          {configFee && (
            <>
              <Form.Item label="Fees payable by" style={{ marginTop: 16 }}>
                <Radio.Group
                  value={feesPayer}
                  onChange={(e) => {
                    setFeesPayer(e.target.value);
                    // Re-validate beneficiary_value when fee payer changes
                    setTimeout(() => {
                      const currentBeneficiaryValue = form.getFieldValue('beneficiary_value');
                      if (currentBeneficiaryValue) {
                        form.validateFields(['beneficiary_value']).catch(() => {
                          // Validation errors are expected
                        });
                      }
                    }, 100);
                  }}
                  disabled={isFormDisabled}
                >
                  <Radio.Button value="SENDER">Sender</Radio.Button>
                  <Radio.Button value="BENEFICIARY">Beneficiary</Radio.Button>
                </Radio.Group>
              </Form.Item>
              {/* Display fee breakdown */}
              {(transferFee !== null || serviceFee !== null) && (
                <div style={{ marginTop: 16, padding: '16px', backgroundColor: '#f5f5f5', borderRadius: 4, marginBottom: 16 }}>
                  <div style={{ marginBottom: 8 }}>
                    <Typography.Text style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.85)' }}>
                      <strong>Transfer fee:</strong> {
                        transferFeeValue
                      }
                    </Typography.Text>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <Typography.Text style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.85)' }}>
                      <strong>Service fee:</strong> {serviceFeeValue}
                    </Typography.Text>
                  </div>
                  <div style={{ marginBottom: 8, borderTop: '1px solid #d9d9d9', paddingTop: 8 }}>
                    <Typography.Text style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.85)' }}>
                      <strong>Total fee:</strong> {
                        totalFee
                      }
                    </Typography.Text>
                  </div>
                  {paymentValue && (
                    <>
                      <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#e6f7ff', borderRadius: 4 }}>
                        <Typography.Text style={{ fontSize: 16, fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.85)', display: 'flex', justifyContent: 'space-between' }}>
                          <span>Total to pay:</span>
                          <span style={{ color: '#F28A28' }}> {
                            totalToPay
                          }
                          </span>
                        </Typography.Text>
                      </div>
                      {beneficiaryValue && (
                        <div style={{ marginTop: 8 }}>
                          <Typography.Text style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.85)' }}>
                            <strong>Beneficiary Receive:</strong> <span style={{ color: '#F28A28' }}>{handleBeneficiaryGet}</span>
                          </Typography.Text>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {isAdminRole && (
            <>
              {/* Payment Method Selection - Portfolio or Direct Transfer */}
              <div style={{ marginBottom: 24 }}>
                <Typography style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, color: 'rgba(0, 0, 0, 0.85)' }}>
                  Payment Method
                </Typography>
                <div
                  className={'payment-method-selected-border'}
                  onClick={() => {
                    if (!disabledPortfolio) {
                      // setPaymentMethod(listPaymentMethod.PORTFOLIO);
                      // form?.resetFields([
                      //   'providerWallet',
                      //   'providerBankAccount',
                      //   'serviceProviderId',
                      // ]);
                      // setProviderBankAccountData(undefined);
                      // setProviderWalletData(undefined);
                      // setServiceProviderData(undefined);
                      setIsFirstLoadBalance(false);
                    }
                  }}
                  style={{
                    cursor: disabledPortfolio ? 'default' : 'pointer',
                    marginBottom: 12,
                    padding: '16px',
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid #d9d9d9',
                    backgroundColor: 'transparent',
                  }}
                >
                  <div className='balance-porfolio' style={{ flex: 1 }}>
                    <Typography
                      style={{
                        fontSize: isTablet ? 12 : 14,
                        fontWeight: 600,
                        color: 'rgba(0, 0, 0, 1)',
                        marginBottom: 4,
                      }}>
                      Portfolio - {paymentCurrencySymbol}
                    </Typography>
                    <Typography
                      style={{
                        fontSize: isTablet ? 10 : 12,
                        color: 'rgba(0, 0, 0, 1)',
                      }}>
                      Avl. balance:{' '}
                      <span
                        style={{
                          color: disabledPortfolio && !isFormDisabled ? 'red' : 'rgba(0, 0, 0, 1)'
                        }}>
                        {formatNumber(
                          avlBalance?.data?.data?.totalBalance || 0,
                          paymentDecimal
                        )}
                      </span>{' '}
                      {paymentCurrencySymbol}
                    </Typography>
                  </div>
                </div>
              </div>
              {/* Service Provider - only show when Direct Transfer is selected */}
              {(paymentMethod === listPaymentMethod.DIRECTER_TRANSFER || paymentMethod === listPaymentMethod.PORTFOLIO) && (
                <>
                  <Form.Item
                    label="Provider"
                    name='serviceProviderId'
                    rules={[
                      // {
                      //   required: true,
                      //   message: 'This field is required',
                      // },
                      ...paymentRequestServiceProviderRules(serviceProviderData, nextStatus)
                    ]}
                  >
                    <AutoComplete
                      enableNotHasData={true}
                      resource='api/v1/companies/defaults'
                      additionalFilters={[
                        companySearchFieldNameFilter,
                        ...(paymentCurrencyId ? [{
                          field: 'currencyId',
                          operator: 'eq' as const,
                          value: paymentCurrencyId,
                        }] : []),
                      ]}
                      mapItem={mapCompanyItem}
                      onSelect={onSelectServiceProvider}
                      allowClear
                      refetchOnEmptyValue
                      onClear={clearServiceProviderData}
                      disabled={isFormDisabled}
                      clearOnBlurIfUnselected
                    >
                      <Input placeholder='Service provider' size='large' disabled={isFormDisabled} suffix={<DownOutlined />} />
                    </AutoComplete>
                  </Form.Item>
                  <Details
                    show={showServiceProviderDetails}
                    setShow={setShowServiceProviderDetails}
                    name={'provider'}
                    fields={companyDetailsFields}
                    data={serviceProviderData}
                  />
                  {/* Provider Bank Account or Wallet - only show when provider is selected */}
                  {serviceProviderData?.id && (
                    <>
                      {/* Show Bank Account for FIAT currency */}
                      {paymentCurrencyData?.type === 'FIAT' ? (
                        <Form.Item
                          label="Provider Bank Account"
                          name='providerBankAccount'
                          rules={[
                            {
                              required: true,
                              message: 'This field is required',
                            },
                          ]}
                        >
                          <AutoComplete
                            enableNotHasData={true}
                            resource={`api/v1/bank_accounts/companies/${serviceProviderData.id}/get-list`}
                            disabled={!serviceProviderData?.id || !customerData?.id || isFormDisabled}
                            additionalFilters={[
                              ...(paymentCurrencyId ? [{
                                field: '_currencyId',
                                operator: 'eq' as const,
                                value: paymentCurrencyId,
                              }] : []),
                              ...(customerData?.id ? [{
                                field: '_userId',
                                operator: 'eq' as const,
                                value: customerData.id,
                              }] : []),
                            ]}
                            mapItem={(item: any, index: number) => ({
                              value: item?.id || index,
                              key: item?.id || index,
                              label: (
                                <Typography.Text>
                                  {item?.accountNumber ? `${item.accountNumber} [${item.currencyCode || ''}]` : `Item ${index + 1}`}
                                </Typography.Text>
                              ),
                              data: item,
                            })}
                            onSelect={onSelectProviderBankAccount}
                            allowClear
                            refetchOnEmptyValue
                            onClear={clearProviderBankAccountData}
                            enableLoadMore={true}
                            pageSize={10}
                            dropdownButtonLabel='View more'
                          >
                            <Input placeholder='Provider Bank Account' size='large' disabled={isFormDisabled} suffix={<DownOutlined />} />
                          </AutoComplete>
                        </Form.Item>
                      ) : (
                        /* Show Wallet for CRYPTO currency */
                        <Form.Item
                          label="Provider Wallet Address"
                          name='providerWallet'
                          rules={[
                            {
                              required: true,
                              message: 'This field is required',
                            },
                          ]}
                        >
                          <AutoComplete
                            enableNotHasData={true}
                            resource={`api/v1/company-wallets/companies/${serviceProviderData.id}/get-list`}
                            disabled={!serviceProviderData?.id || !customerData?.id || isFormDisabled}
                            additionalFilters={[
                              ...(paymentCurrencyId ? [{
                                field: '_currencyId',
                                operator: 'eq' as const,
                                value: paymentCurrencyId,
                              }] : []),
                              ...(customerData?.id ? [{
                                field: '_userId',
                                operator: 'eq' as const,
                                value: customerData.id,
                              }] : []),
                            ]}
                            mapItem={(item: any, index: number) => ({
                              value: item?.id || index,
                              key: item?.id || index,
                              label: (
                                <Typography.Text>
                                  {item?.address || item?.name || `Item ${index + 1}`}
                                </Typography.Text>
                              ),
                              data: item,
                            })}
                            onSelect={onSelectProviderWallet}
                            allowClear
                            refetchOnEmptyValue
                            onClear={clearProviderWalletData}
                            enableLoadMore={true}
                            pageSize={10}
                            dropdownButtonLabel='View more'
                          >
                            <Input placeholder='Provider Wallet Address' size='large' disabled={isFormDisabled} suffix={<DownOutlined />} />
                          </AutoComplete>
                        </Form.Item>
                      )}
                      {/* Show details when selected */}
                      {providerBankAccountData && paymentCurrencyData?.type === 'FIAT' && (
                        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <Typography style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.85)' }}>
                            Currency: {providerBankAccountData?.currencyCode}
                          </Typography>
                          <Typography style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.85)' }}>
                            Account name: {serviceProviderData?.name}
                          </Typography>
                          {providerBankAccountData?.bank && (
                            <>
                              <Typography style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.85)' }}>
                                Bank name: {providerBankAccountData.bank.name}
                              </Typography>
                              {providerBankAccountData.bank.swift && (
                                <Typography style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.85)' }}>
                                  SWIFT: {providerBankAccountData.bank.swift}
                                </Typography>
                              )}
                              {providerBankAccountData.bank.legalAddress && (
                                <Typography style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.85)' }}>
                                  Bank Address: {providerBankAccountData.bank.legalAddress}
                                </Typography>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      {providerWalletData && paymentCurrencyData?.type === 'CRYPTO' && (
                        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <Typography style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.85)' }}>
                            Currency: {providerWalletData?.assetConfig?.symbol}
                          </Typography>
                          <Typography style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.85)' }}>
                            Network: {providerWalletData?.network?.name}
                          </Typography>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
          <Form.Item
            label="Send Date"
            name='dateOfTransaction'
            rules={dateOfTransactionRules}
            getValueProps={getDateValueProps}
          >
            <DefaultDatePicker
              placeholder='Send Date'
              style={dateOfTransactionFieldStyle}
              disabled={isFormDisabled}
            />
          </Form.Item>
          {isAdminRole && (
            <SelectField
              style={{ marginTop: '8px' }}
              label="Purpose of remittance"
              name='purposeOfRemittance'
              getOptionLabel={getPurposeOfRemittanceOptionLabel}
              items={purposeOfRemittanceItems}
              placeholder={purposeOfRemittancePlaceholder}
              allowClear
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                },
                ...paymentRequestPurposeOfRemittanceRules(nextStatus)
              ]}
              disabled={isFormDisabled}
            />
          )}
          <Form.Item
            label="Special instructions"
            name='specialInstructions'
            validateTrigger='onBlur'
            rules={[
              {
                required: true,
                message: 'This field is required',
              },
              {
                max: 256,
                message: 'Maximum 256 characters',
                validateTrigger: 'onBlur',
              },
            ]}
            style={{ marginTop: '8px' }}
          >
            <Input.TextArea
              rows={1}
              placeholder='Special instructions'
              onChange={(e) => {
                const currentValue = e.target.value;
                const idUserPrefix = `Ref ${customerData?.smartUID}` || '';
                if (!currentValue.startsWith(idUserPrefix)) {
                  const finalValue = idUserPrefix;
                  form.setFieldValue('specialInstructions', finalValue);
                  setInstructionsData(finalValue);
                } else {
                  form.setFieldValue('specialInstructions', currentValue);
                  setInstructionsData(currentValue);
                }
              }}
              allowClear
              disabled={isFormDisabled}
            />
          </Form.Item>
          <Col>
            <Form.Item
              label="Supporting documents"
              name='documents'
              valuePropName='fileList'
              getValueFromEvent={getFileListFromEvent}
              rules={[
                {
                  validator: async (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.resolve();
                    }

                    // Validate file count
                    const MAX_FILE_COUNT = 5;
                    if (value.length > MAX_FILE_COUNT) {
                      return Promise.reject(new Error(`Maximum ${MAX_FILE_COUNT} files allowed`));
                    }

                    // Validate file types (images, PDF, Word, Excel — same as beforeUpload)
                    const invalidFiles = value.filter((file: UploadFile) => {
                      const fileType = file.type || (file.originFileObj as any)?.type;
                      return fileType && !allowedUploadTypes.includes(fileType);
                    });

                    if (invalidFiles.length > 0) {
                      return Promise.reject(
                        new Error(
                          'Allowed: images (JPG, PNG, WEBP, GIF, BMP, TIFF), PDF, Word (.doc, .docx), Excel (.xls, .xlsx)',
                        ),
                      );
                    }

                    // Validate file sizes (10MB max)
                    const MAX_SIZE = 10 * 1024 * 1024;
                    const oversizedFiles = value.filter((file: UploadFile) => {
                      const fileSize = file.size || (file.originFileObj as any)?.size;
                      return fileSize && fileSize > MAX_SIZE;
                    });

                    if (oversizedFiles.length > 0) {
                      return Promise.reject(new Error('File size must be less than 10 MB'));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
              className='upload-supporting-documents'
            >
              <Upload
                accept={acceptUploadFileTypes}
                action={`${ROOT_URL}/api/v1/storage/upload`}
                listType='text'
                headers={{
                  Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
                }}
                multiple={true}
                beforeUpload={beforeUpload}
                onChange={onChangeUpload}
                onRemove={handleRemoveFile}
                fileList={fileList}
                disabled={isFormDisabled}
                showUploadList={{
                  showRemoveIcon: !isFormDisabled,
                }}
              >
                <Button icon={<UploadOutlined />} style={uploadButtonStyle} disabled={isFormDisabled || fileList.length >= 5}>
                  &nbsp;Click to upload supporting documents
                </Button>
              </Upload>
            </Form.Item>
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              Maximum 5 files allowed. Supported formats: images, PDF, Word, Excel. Max size: 10 MB per file.
            </Typography.Text>
          </Col>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
            <Button onClick={handleCancel} disabled={isCreating}>
              Cancel
            </Button>
            <Button
              type='primary'
              onClick={handleSubmit('save')}
              loading={isCreating}
              disabled={isFormEmpty || isUploading || isFormDisabled || beneficiaryValueError || paymentValueError}
            >
              Submit Order
            </Button>
            {/* {!isAdminRole && (
              <Button
                type='primary'
                onClick={handleSubmit('send')}
                loading={isCreating}
                disabled={isFormEmpty || isUploading || isFormDisabled || beneficiaryValueError || paymentValueError}
              >
                Send
              </Button>
            )} */}
          </div>
        </Form>
      </div>
      {alertContextHolder}
      <NewCompanyModalCreate
        close={createCompanyModalClose}
        formProps={createCompanyFormProps}
        modalProps={createCompanyModalProps}
        formIsLoading={createCompanyFormLoading}
        formSubmit={createCompanyFormSubmit}
        changeCompanyData={changeCompanyData}
      />
      <NewBankModalCreate
        close={createBankModalClose}
        formProps={createBankFormProps}
        modalProps={createBankModalProps}
        formIsLoading={createBankFormLoading}
        formSubmit={createBankFormSubmit}
        changeBankData={changeBankData}
      />
      <NewAccountModalCreate
        close={createBankAccountModalClose}
        formProps={createBankAccountFormProps}
        modalProps={createBankAccountModalProps}
        formIsLoading={createBankAccountFormLoading}
        formSubmit={createBankAccountFormSubmit}
        changeBankAccountData={changeBankAccountData}
        bankData={bankData}
      />
      <PaymentConfirmationModal
        modalOpened={paymentConfirmationModalOpened}
        closeModal={() => {
          if (paymentRejected.current) {
            setPaymentConfirmationModalOpened(false);
            paymentRejected.current();
          }
        }}
        paymentDetails={{
          fee,
          userWallet,
          ethPrice,
          walletToTransferFundsTo: form?.getFieldValue('cryptoWallet'),
          tokensToTransfer: form?.getFieldValue('amount'),
        }}
        onSubmit={submitPayment}
      />

      <Modal
        open={!!showReviewFee}
        title="Review Fee"
        className='smartModalSM'
        closeIcon={false}
        onOk={() => {
          if (beneficiaryCurrencyId) {
            fetchConfigurationFee(beneficiaryCurrencyId);
          }
          setShowReviewFee(false)
        }}
        cancelButtonProps={{
          style: {
            display: 'none',
          },
        }}
      >
        <p>Fee or beneficiary receive has been updated. Take a moment to review before continuing.</p>
      </Modal>
    </>
  );
};

// Helper hooks and functions for Company Default
export const useMetadataCompaniesDefault = (params: {
  currencyId: number | undefined;
  searchName?: string;
}) => {
  const { data = { data: [] }, isLoading } = useGetCompanyDefault({
    currencyId: params?.currencyId,
    name: params?.searchName,
  });

  return { dataCompaniesDefault: data?.data, isLoading };
};

export const useGetCompanyDefault = (params: {
  currencyId: number | undefined;
  name?: string;
}) => {
  return useQuery<{ data: any }>(
    [`/api/v1/companies/defaults`, params?.currencyId, params?.name],
    async () => getCompanyDefault(params),
    {
      enabled: !!params?.currencyId,
    }
  );
};

export const useGetCompanyDefaultMutation = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: getCompanyDefault,
  });
};

export function getCompanyDefault(params: {
  currencyId: number | undefined;
  name?: string;
}) {
  const param = new URLSearchParams();
  if (params?.currencyId)
    param.append('currencyId', String(params?.currencyId));
  if (params.name) param.append('_like', params.name);
  return httpClient.get(
    `${ROOT_URL}/api/v1/companies/defaults?${param.toString()}`
  );
}

// Helper hooks for Wallet and Bank Account Provider
export const useWalletManagementProvider = (
  companyId: number,
  params: { start: number; end: number },
  name?: string,
  currencyId?: number
) => {
  const {
    data = {
      data: {
        data: { total: 0, pageSize: 10, page: 1, totalPages: 1, items: [] },
      },
    },
    isLoading,
    refetch,
  } = useGetListWalletProvider({
    companyId,
    ...params,
    name,
    currencyId,
  });
  return { dataListWalletProvider: data.data.data, isLoading, refetch };
};

export const useGetListWalletProvider = (params: {
  companyId: number;
  start: number;
  end: number;
  name?: string;
  currencyId?: number;
}) => {
  return useQuery<{
    data: {
      data: {
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
        items: [];
      };
    };
  }>(
    [
      `/api/v1/company-wallets/companies/${params.companyId}/get-list`,
      params.start,
      params.end,
      params.name,
      params.currencyId,
    ],
    async () => getListWalletProvider(params),
    {
      enabled: !!params.companyId,
    }
  );
};

export function getListWalletProvider(params: {
  companyId: number;
  start: number;
  end: number;
  name?: string;
  currencyId?: number;
}) {
  const param = new URLSearchParams();
  param.append('_start', String(params.start));
  param.append('_end', String(params.end));
  if (params.name) param.append('_like', params.name);
  if (params.currencyId) param.append('_currencyId', String(params.currencyId));
  return httpClient.get(
    `${ROOT_URL}/api/v1/company-wallets/companies/${params.companyId}/get-list?${param.toString()}`
  );
}

export const useBankAccountManagementProvider = (
  companyId: number,
  params: { start: number; end: number },
  name?: string,
  currencyId?: number
) => {
  const {
    data = {
      data: {
        data: { total: 0, pageSize: 10, page: 1, totalPages: 1, items: [] },
      },
    },
    isLoading,
    refetch,
  } = useGetListBankAccountProvider({
    companyId,
    ...params,
    name,
    currencyId,
  });
  return { dataListBankAccountProvider: data.data.data, isLoading, refetch };
};

export const useGetListBankAccountProvider = (params: {
  companyId: number;
  start: number;
  end: number;
  name?: string;
  currencyId?: number;
}) => {
  return useQuery<{
    data: {
      data: {
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
        items: [];
      };
    };
  }>(
    [
      `/api/v1/bank_accounts/companies/${params.companyId}/get-list`,
      params.start,
      params.end,
      params.name,
      params.currencyId,
    ],
    async () => getListBankAccountProvider(params),
    {
      enabled: !!params.companyId,
    }
  );
};

export function getListBankAccountProvider(params: {
  companyId: number;
  start: number;
  end: number;
  name?: string;
  currencyId?: number;
}) {
  const param = new URLSearchParams();
  param.append('_start', String(params.start));
  param.append('_end', String(params.end));
  if (params.name) param.append('_like', params.name);
  if (params.currencyId) param.append('_currencyId', String(params.currencyId));
  return httpClient.get(
    `${ROOT_URL}/api/v1/bank_accounts/companies/${params.companyId}/get-list?${param.toString()}`
  );
}

